import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { motion, Reorder } from 'framer-motion'
import {
  Plus,
  Trash2,
  GripVertical,
  Clock,
  Video,
  Link as LinkIcon,
  Lock,
  Calendar,
  Save,
  Play,
  AlertCircle,
  CheckCircle,
  Loader2,
  Layers,
  Mic,
  X,
  FileDown,
  FileText,
  ChevronDown,
  List,
  Info,
  Repeat,
  CalendarDays,
  Send,
  Globe,
  Sparkles,
} from 'lucide-react'
import Sidebar from '../components/layout/Sidebar'
import DocumentIntelligencePanel from '../components/meeting/DocumentIntelligencePanel'
import { generateMockMeeting } from '../services/mockData'
import { authAPI, cppBotAPI, zoomAPI, meetingsAPI, agendaAPI, teamsAPI, internalAgendaAPI } from '../services/api'
import { useToast } from '../components/common/Toast'
import { getDraftMeeting, removeDraftMeeting, upsertDraftMeeting } from '../services/draftStorage'
import { useAuth } from '../context/AuthContext'
import { useZoomOAuth } from '../context/ZoomOAuthContext'
import { format, parseISO, isValid } from 'date-fns'
import { decodeHtmlEntities, formatMeetingTypeDisplayName } from '../utils/displayText'
import { filterTimezoneOptions, getTimezoneLabelParts } from '../utils/timezoneLabels'
import MeetingSetupOverlay from '../components/common/MeetingSetupOverlay'
import { requestOnboardingVideosCheck } from '../constants/onboardingVideos'
import {
  buildInternalAgendaStateFromGoals,
  emptyInternalAgendaState,
  INTERNAL_DESIRED_OUTCOMES,
  mapApiInternalAgendaToGoals,
  newInternalGoalId,
} from '../utils/internalAgendaBuilder'
import { QUICK_START_ROUTE } from '../utils/quickStartFlow'
import { useScheduleConflicts } from '../hooks/useScheduleConflicts'
import ScheduleConflictBanner from '../components/common/ScheduleConflictBanner'
import ScheduleConflictModal from '../components/common/ScheduleConflictModal'

/** Host is implicit; never list the organizer as a participant (avoids duplicate invite emails). */
function withoutOrganizerParticipants(participants, organizerEmail) {
  const o = (organizerEmail ?? '').trim().toLowerCase()
  if (!o) return participants ?? []
  return (participants ?? []).filter((p) => (p.email ?? '').trim().toLowerCase() !== o)
}

function getOrganizerTimezone(user) {
  const fromUser = user?.timezone?.trim?.()
  if (fromUser) return fromUser
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

/**
 * Interpret a datetime-local string (e.g. "2026-04-15T15:00") as a time in
 * `ianaTimezone` and return the equivalent UTC ISO string.
 * Falls back to browser-local interpretation if conversion fails.
 */
function localStringToUtcInTimezone(localDateStr, ianaTimezone) {
  try {
    // Treat the entered digits as UTC temporarily so we can compute the offset.
    const asUtc = new Date(localDateStr + ':00Z')
    if (Number.isNaN(asUtc.getTime())) return null
    // Find what the target timezone shows for that "UTC" moment.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(asUtc)
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '00'
    const tzLocalAsUtc = new Date(
      `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}Z`
    )
    // offsetMs is positive for UTC+ zones, negative for UTC-.
    const offsetMs = tzLocalAsUtc.getTime() - asUtc.getTime()
    return new Date(asUtc.getTime() - offsetMs).toISOString()
  } catch {
    const d = new Date(localDateStr)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }
}

/**
 * Format a UTC ISO string for display in a given IANA timezone.
 * Returns a human-readable string like "Mon, Apr 15 • 3:00 PM".
 */
function formatInTimezone(isoString, ianaTimezone) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone,
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(new Date(isoString))
  } catch {
    return ''
  }
}

const DEFAULT_NEW_AGENDA_ITEM_MINUTES = 5

function abAgendaItemSelector(id) {
  const s = String(id ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  return `[data-ab-agenda-item-id="${s}"]`
}

function ordinalCalendarDayLabel(n) {
  const v = n % 100
  if (v >= 11 && v <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

const AGENDA_MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1)

function agendaItemDurationMinutes(item) {
  const n = Number(item?.duration)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

/**
 * When a meeting length is set: slack = minutes still unassigned in the meeting;
 * maxThisItem = minutes this row could use without changing other rows (may exceed slack if this row is short).
 */
function agendaItemTimeBudget(meetingDurationMinutes, agendaItems, index) {
  if (meetingDurationMinutes == null || Number.isNaN(Number(meetingDurationMinutes))) return null
  const items = agendaItems ?? []
  const total = items.reduce((sum, it) => sum + agendaItemDurationMinutes(it), 0)
  const above = items.slice(0, index).reduce((sum, it) => sum + agendaItemDurationMinutes(it), 0)
  const below = items.slice(index + 1).reduce((sum, it) => sum + agendaItemDurationMinutes(it), 0)
  return {
    slackMinutes: meetingDurationMinutes - total,
    maxMinutesThisItem: meetingDurationMinutes - above - below,
  }
}

function AgendaBuilderPage() {
  const { meetingId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const { user } = useAuth()
  const organizerEmailLower = (user?.email ?? '').toLowerCase().trim()
  const { isConnected: zoomConnected, connectZoomUrl } = useZoomOAuth()

  const userDomain = (() => {
    const email = user?.email ?? ''
    const at = email.indexOf('@')
    return at >= 0 && at < email.length - 1 ? email.slice(at + 1).toLowerCase() : 'rithmo.ai'
  })()

  const [meeting, setMeeting] = useState(() =>
    generateMockMeeting({
      title: '',
      description: '',
      agenda: [],
      duration: undefined,
      endTime: undefined,
    })
  )
  const [saving, setSaving] = useState(false)
  const [starting, setStarting] = useState(false)
  const [createdMeeting, setCreatedMeeting] = useState(null) // { meetingId, joinUrl, password, createdWithUserZoom, cppBot: { joined, addBotAfterYouJoin } }
  const [joiningMeeting, setJoiningMeeting] = useState(false)
  // Concurrent-Zoom-call confirmation modal. Promise-based so the async start handler
  // can `await` user choice without changing shape.
  const [concurrentConfirm, setConcurrentConfirm] = useState(null)
  const concurrentConfirmResolverRef = useRef(null)
  const askConcurrentZoomConfirm = useCallback(() => {
    return new Promise((resolve) => {
      concurrentConfirmResolverRef.current = resolve
      setConcurrentConfirm({ open: true })
    })
  }, [])
  const resolveConcurrentConfirm = useCallback((value) => {
    const fn = concurrentConfirmResolverRef.current
    concurrentConfirmResolverRef.current = null
    setConcurrentConfirm(null)
    if (fn) fn(value)
  }, [])
  // Schedule-conflict confirmation modal (same promise-based pattern as concurrent Zoom).
  const [scheduleConflictConfirm, setScheduleConflictConfirm] = useState(null)
  const scheduleConflictResolverRef = useRef(null)
  const askScheduleConflictConfirm = useCallback(() => {
    return new Promise((resolve) => {
      scheduleConflictResolverRef.current = resolve
      setScheduleConflictConfirm({ open: true })
    })
  }, [])
  const resolveScheduleConflictConfirm = useCallback((value) => {
    const fn = scheduleConflictResolverRef.current
    scheduleConflictResolverRef.current = null
    setScheduleConflictConfirm(null)
    if (fn) fn(value)
  }, [])

  const [errors, setErrors] = useState({})

  // Meeting type selection
  const [meetingTypes, setMeetingTypes] = useState([])
  const [selectedMeetingType, setSelectedMeetingType] = useState('default')
  // Recurrence: required — 'one_off' | 'recurring' | null (not yet selected)
  const [recurrenceKind, setRecurrenceKind] = useState(null)
  const [recurringSeriesId, setRecurringSeriesId] = useState(null)
  // Recurrence rule for Zoom (when recurring): type 1=Daily 2=Weekly 3=Monthly
  const [recurrenceRule, setRecurrenceRule] = useState({
    type: 2,
    repeat_interval: 1,
    weekly_days: '',
    monthly_day: '',
    end_times: 10,
    end_date_time: '',
    never_ends: false,
  })
  const [loadingTypes, setLoadingTypes] = useState(true)
  const [loadingMeeting, setLoadingMeeting] = useState(!!meetingId)
  const [isRecurringParent, setIsRecurringParent] = useState(false)
  const [isEditingOneOff, setIsEditingOneOff] = useState(false)

  const [participantInput, setParticipantInput] = useState('')
  const [pendingParticipantsQueue, setPendingParticipantsQueue] = useState([])
  const [pendingParticipantName, setPendingParticipantName] = useState('')
  const pendingParticipantsTotalRef = useRef(null)
  const [knownContactsMap, setKnownContactsMap] = useState(() => new Map())
  // Import meeting: searchable dropdown
  const [importSearch, setImportSearch] = useState('')
  const [importDropdownOpen, setImportDropdownOpen] = useState(false)
  const [importMeetings, setImportMeetings] = useState([])
  const [importingMeeting, setImportingMeeting] = useState(false)
  const [loadingImportList, setLoadingImportList] = useState(false)
  const [importDropdownRect, setImportDropdownRect] = useState(null)
  const importInputRef = useRef(null)
  // Import agenda only: searchable dropdown
  const [agendaImportSearch, setAgendaImportSearch] = useState('')
  const [agendaImportDropdownOpen, setAgendaImportDropdownOpen] = useState(false)
  const [agendaImportMeetings, setAgendaImportMeetings] = useState([])
  const [agendaImportingMeeting, setAgendaImportingMeeting] = useState(false)
  const [loadingAgendaImportList, setLoadingAgendaImportList] = useState(false)
  const [agendaImportDropdownRect, setAgendaImportDropdownRect] = useState(null)
  const agendaImportInputRef = useRef(null)
  const titleInputRef = useRef(null)
  const meetingDetailsSectionRef = useRef(null)
  const agendaSectionRef = useRef(null)
  /** After Add Item: scroll to and focus this agenda row once. */
  const pendingScrollAbAgendaItemIdRef = useRef(null)
  const participantsSectionRef = useRef(null)
  const startTimeInputRef = useRef(null)
  const [durationDropdownItemId, setDurationDropdownItemId] = useState(null)
  const durationDropdownRef = useRef(null)
  const [governanceInfoTooltip, setGovernanceInfoTooltip] = useState(false)
  const governanceInfoTooltipTimeoutRef = useRef(null)
  const bulletInputRefs = useRef({})
  const DURATION_PRESETS = [5, 10, 15, 20, 30, 45, 60]
  const MEETING_DURATION_PRESETS = [15, 30, 45, 60, 90]
  const [meetingDurationDropdownOpen, setMeetingDurationDropdownOpen] = useState(false)
  const meetingDurationDropdownRef = useRef(null)
  // IANA zone for the start/end pickers: wall times are interpreted in this zone (not the browser’s local zone).
  // Sent as `organizerTimezone` for Zoom + emails; changing the dropdown does not update the user profile.
  const [schedulingTimezone, setSchedulingTimezone] = useState(() => getOrganizerTimezone(null))
  const [tzDropdownOpen, setTzDropdownOpen] = useState(false)
  const [tzSearch, setTzSearch] = useState('')
  const tzDropdownRef = useRef(null)
  /** Builder dropdown first, then profile / browser — used for parsing, display, and API. */
  const effectiveSchedulingTimezone = (schedulingTimezone?.trim?.() || getOrganizerTimezone(user) || '').trim()
  const schedulingTzButtonLabel = effectiveSchedulingTimezone
    ? getTimezoneLabelParts(effectiveSchedulingTimezone).label
    : 'Set timezone'
  /** External meetings: builder lens for public agenda vs private internal goals */
  const [builderAgendaLens, setBuilderAgendaLens] = useState('public')
  const [internalGoals, setInternalGoals] = useState([])
  const [internalAgendaLoading, setInternalAgendaLoading] = useState(false)
  const [internalAgendaLoadError, setInternalAgendaLoadError] = useState(null)
  const [teamsList, setTeamsList] = useState([])
  const [selectedTeamIds, setSelectedTeamIds] = useState([])
  // Cache of team members keyed by teamId so we only fetch each team once
  const teamMembersCacheRef = useRef({})

  const totalDuration = meeting.agenda.reduce((sum, item) => sum + (item.duration || 0), 0)
  const durationValid = meeting.duration == null || meeting.duration >= totalDuration
  const canAddAgendaItemWithinBudget =
    meeting.duration == null || totalDuration + DEFAULT_NEW_AGENDA_ITEM_MINUTES <= meeting.duration
  const participantsWithEmail = (meeting.participants ?? []).filter((p) => (p.email ?? '').trim())
  const isFormValidForCreate = Boolean(
    meeting.title.trim() &&
    recurrenceKind !== null &&
    meeting.agenda.length >= 1 &&
    meeting.agenda.every((item) => item.title.trim()) &&
    durationValid &&
    participantsWithEmail.length >= 1
  )

  const docIntelShowSuggestions = useMemo(
    () => (meeting.audienceMode ?? 'internal') !== 'external' || builderAgendaLens === 'public',
    [meeting.audienceMode, builderAgendaLens],
  )

  const reloadAgendaFromServer = useCallback(async () => {
    if (!meetingId) return
    try {
      const meetingRes = await meetingsAPI.get(meetingId)
      const data = meetingRes?.data ?? meetingRes
      if (!data?.meetingId) return
      const agendaList = data.agenda ?? []
      setMeeting((prev) => ({
        ...prev,
        agenda: agendaList.map((item, index) => ({
          id: item.id ?? `item_${index}`,
          title: decodeHtmlEntities(item.title ?? ''),
          duration: item.durationSec ? Math.round(item.durationSec / 60) : (item.duration ?? 5),
          order: item.order ?? index,
          details: decodeHtmlEntities(item.description ?? ''),
          detailsAsBullets: true,
        })),
      }))
    } catch {
      /* ignore */
    }
  }, [meetingId])

  const scrollToFirstIncomplete = () => {
    const newErrors = {}
    if (!meeting.title.trim()) newErrors.title = 'Title is required'
    if (recurrenceKind === null) newErrors.recurrence = 'Select One-off or Recurring'
    if (meeting.agenda.length === 0) newErrors.agenda = 'Add at least one agenda item'
    meeting.agenda.forEach((item, index) => {
      if (!item.title.trim()) newErrors[`agenda_${index}`] = 'Item title is required'
    })
    if (meeting.duration != null && meeting.duration < totalDuration) {
      newErrors.duration = 'Duration cannot be less than agenda total'
    }
    if (participantsWithEmail.length === 0) {
      newErrors.participants = 'Add at least one participant to create the meeting'
    }
    setErrors(newErrors)
    if (newErrors.title) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          meetingDetailsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          titleInputRef.current?.focus()
        })
      })
      return
    }
    if (newErrors.recurrence) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          meetingDetailsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      })
      return
    }
    if (newErrors.duration) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          meetingDetailsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      })
      return
    }
    if (newErrors.agenda || Object.keys(newErrors).some((k) => k.startsWith('agenda_'))) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          agendaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      })
      return
    }
    if (newErrors.participants) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          participantsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      })
    }
  }

  // Participant suggestions come entirely from knownContactsMap (loaded from the API).
  const participantDirectory = []

  // Load existing meeting when editing (meetingId in URL) – from DB or local draft
  useEffect(() => {
    if (!meetingId) return
    setLoadingMeeting(true)
    meetingsAPI
      .get(meetingId)
      .then((meetingRes) => {
        const data = meetingRes?.data ?? meetingRes
        if (data?.meetingId) {
          setIsRecurringParent(Boolean(data.isRecurring && !data.parentMeetingId))
          setIsEditingOneOff(Boolean(!data.isRecurring && !data.parentMeetingId))
          const agendaList = data.agenda ?? []
          const participantList = withoutOrganizerParticipants(data.participants ?? [], user?.email)
          const startMs = data.scheduledStartTime ? new Date(data.scheduledStartTime).getTime() : null
          const endMs = data.scheduledEndTime ? new Date(data.scheduledEndTime).getTime() : null
          const durationMin = (startMs != null && endMs != null && endMs > startMs)
            ? Math.round((endMs - startMs) / 60000)
            : undefined
          setMeeting((prev) => ({
            ...prev,
            startTime: data.scheduledStartTime ?? prev.startTime,
            endTime: data.scheduledEndTime ?? prev.endTime,
            duration: durationMin ?? prev.duration,
            id: meetingId,
            meetingId,
            title: decodeHtmlEntities(data.title ?? data.meetingId ?? prev.title ?? ''),
            description: decodeHtmlEntities(data.description ?? prev.description ?? ''),
            agenda: agendaList.map((item, index) => ({
              id: item.id ?? `item_${index}`,
              title: decodeHtmlEntities(item.title ?? ''),
              duration: item.durationSec ? Math.round(item.durationSec / 60) : (item.duration ?? 5),
              order: item.order ?? index,
              details: decodeHtmlEntities(item.description ?? ''),
              detailsAsBullets: true,
            })),
            participants: participantList.map((p) => ({
              id: p.participantId ?? p.id,
              name: p.displayName ?? p.name ?? p.email ?? '',
              email: p.email ?? '',
              role: p.role,
            })),
            governanceSettings: data.governanceConfig
              ? {
                  talkEquity: { enabled: data.governanceConfig.talkEquity?.enabled !== false },
                  interruptionPrevention: { enabled: data.governanceConfig.interruptionPrevention?.enabled !== false },
                  agendaEnforcement: { enabled: data.governanceConfig.agendaEnforcement?.enabled !== false },
                }
              : prev.governanceSettings,
            autoEndMeeting: data.governanceConfig?.autoEndMeeting === true,
            demoMode: data.governanceConfig?.demoMode === true,
            requireOutputOwnership: data.governanceConfig?.requireOutputOwnership === true,
            audienceMode: (() => {
              const raw = data.audienceMode ?? data.audience_mode
              if (raw === 'external') return 'external'
              if (raw === 'internal') return 'internal'
              return prev?.audienceMode === 'external' ? 'external' : 'internal'
            })(),
          }))
          if (data.meetingTypeId) setSelectedMeetingType(data.meetingTypeId)
          setRecurrenceKind(data.isRecurring ? 'recurring' : 'one_off')
          setRecurringSeriesId(data.recurringSeriesId ?? null)
          if (data.recurrenceRule && typeof data.recurrenceRule === 'object') {
            const nr = data.recurrenceRule
            const isNeverEnds = nr.never_ends === true
            setRecurrenceRule((prev) => ({
              type: nr.type ?? prev.type,
              repeat_interval: nr.repeat_interval ?? prev.repeat_interval,
              weekly_days: nr.weekly_days ?? prev.weekly_days,
              monthly_day: nr.monthly_day ?? prev.monthly_day,
              end_times: isNeverEnds ? 0 : (nr.end_times ?? prev.end_times),
              end_date_time: isNeverEnds ? '' : (nr.end_date_time ?? prev.end_date_time),
              never_ends: isNeverEnds,
            }))
          }
        }
      })
      .catch(() => {
        setIsRecurringParent(false)
        setIsEditingOneOff(false)
        const localDraft = getDraftMeeting(meetingId)
        if (localDraft) {
          setMeeting((prev) => ({
            ...prev,
            id: localDraft.id,
            meetingId,
            title: decodeHtmlEntities(localDraft.title ?? prev.title),
            description: decodeHtmlEntities(localDraft.description ?? ''),
            startTime: localDraft.startTime ?? prev.startTime,
            endTime: localDraft.endTime ?? prev.endTime,
            duration: localDraft.duration ?? prev.duration,
            agenda: (localDraft.agenda ?? []).map((item, index) => ({
              id: item.id ?? `item_${index}`,
              title: decodeHtmlEntities(item.title ?? ''),
              duration: item.duration ?? 5,
              order: item.order ?? index,
              details: item.details ?? '',
              detailsAsBullets: item.detailsAsBullets !== false,
            })),
            participants: withoutOrganizerParticipants(localDraft.participants ?? [], user?.email),
          }))
          if (localDraft.meetingType) setSelectedMeetingType(localDraft.meetingType)
          setRecurrenceKind(localDraft.isRecurring === true ? 'recurring' : localDraft.isRecurring === false ? 'one_off' : null)
          setRecurringSeriesId(localDraft.recurringSeriesId ?? null)
          if (localDraft.recurrenceRule && typeof localDraft.recurrenceRule === 'object') {
            const nr = localDraft.recurrenceRule
            const isNeverEnds = nr.never_ends === true
            setRecurrenceRule((prev) => ({
              type: nr.type ?? prev.type,
              repeat_interval: nr.repeat_interval ?? prev.repeat_interval,
              weekly_days: nr.weekly_days ?? prev.weekly_days,
              monthly_day: nr.monthly_day ?? prev.monthly_day,
              end_times: isNeverEnds ? 0 : (nr.end_times ?? prev.end_times),
              end_date_time: isNeverEnds ? '' : (nr.end_date_time ?? prev.end_date_time),
              never_ends: isNeverEnds,
            }))
          }
        }
      })
      .finally(() => setLoadingMeeting(false))
  }, [meetingId, user?.email])

  useEffect(() => {
    if ((meeting.audienceMode ?? 'internal') !== 'external') {
      setBuilderAgendaLens('public')
    }
  }, [meeting.audienceMode])

  useEffect(() => {
    if (!meetingId || loadingMeeting) return
    if ((meeting.audienceMode ?? 'internal') !== 'external') {
      setInternalGoals([])
      setInternalAgendaLoadError(null)
      return
    }
    let cancelled = false
    setInternalAgendaLoading(true)
    setInternalAgendaLoadError(null)
    internalAgendaAPI
      .get(meetingId)
      .then((res) => {
        const data = res?.data ?? res
        const ia = data?.internalAgenda
        if (cancelled) return
        if (ia) setInternalGoals(mapApiInternalAgendaToGoals(ia))
        else setInternalGoals([])
      })
      .catch(() => {
        if (!cancelled) {
          setInternalAgendaLoadError(
            'Could not load internal goals. Check access or try again — public agenda is still editable.'
          )
        }
      })
      .finally(() => {
        if (!cancelled) setInternalAgendaLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [meetingId, loadingMeeting, meeting.audienceMode])

  // Prefill from "Import from Calendar" (navigate with state.importEvent)
  useEffect(() => {
    const importEvent = location.state?.importEvent
    if (!importEvent || meetingId) return
    const startMs = importEvent.startAt ? new Date(importEvent.startAt).getTime() : null
    const endMs = importEvent.endAt ? new Date(importEvent.endAt).getTime() : null
    const durationMin = (startMs != null && endMs != null && endMs > startMs)
      ? Math.round((endMs - startMs) / 60000)
      : undefined
    setMeeting((prev) => ({
      ...prev,
      title: importEvent.title ? decodeHtmlEntities(importEvent.title) : prev.title,
      startTime: importEvent.startAt ?? prev.startTime,
      endTime: importEvent.endAt ?? prev.endTime,
      duration: durationMin ?? prev.duration,
    }))
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state?.importEvent, location.pathname, meetingId, navigate])

  useEffect(() => {
    teamsAPI.list().then((res) => {
      const data = res?.data ?? res
      setTeamsList(data?.teams ?? [])
    }).catch(() => setTeamsList([]))
  }, [])

  // Remove organizer from participant list if present (e.g. legacy draft or API copy including host).
  useEffect(() => {
    const o = (user?.email ?? '').toLowerCase().trim()
    if (!o) return
    setMeeting((prev) => {
      const list = prev.participants ?? []
      const filtered = list.filter((p) => (p.email ?? '').trim().toLowerCase() !== o)
      if (filtered.length === list.length) return prev
      return { ...prev, participants: filtered }
    })
  }, [user?.email])

  // Update dropdown position when open (e.g. after scroll or layout shift)
  useEffect(() => {
    if (!importDropdownOpen || !importInputRef.current) return
    const updateRect = () => {
      if (importInputRef.current) {
        setImportDropdownRect(importInputRef.current.getBoundingClientRect())
      }
    }
    updateRect()
    window.addEventListener('scroll', updateRect, true)
    window.addEventListener('resize', updateRect)
    return () => {
      window.removeEventListener('scroll', updateRect, true)
      window.removeEventListener('resize', updateRect)
    }
  }, [importDropdownOpen, loadingImportList])

  // Fetch meetings for agenda import dropdown when opened or search changes
  useEffect(() => {
    if (!agendaImportDropdownOpen) return
    const timer = setTimeout(() => {
      setLoadingAgendaImportList(true)
      meetingsAPI
        .listForImport(agendaImportSearch)
        .then((res) => {
          const list = res?.data?.meetings ?? res?.meetings ?? []
          setAgendaImportMeetings(list)
        })
        .catch(() => setAgendaImportMeetings([]))
        .finally(() => setLoadingAgendaImportList(false))
    }, 200)
    return () => clearTimeout(timer)
  }, [agendaImportDropdownOpen, agendaImportSearch])

  // Update agenda import dropdown position when open
  useEffect(() => {
    if (!agendaImportDropdownOpen || !agendaImportInputRef.current) return
    const updateRect = () => {
      if (agendaImportInputRef.current) {
        setAgendaImportDropdownRect(agendaImportInputRef.current.getBoundingClientRect())
      }
    }
    updateRect()
    window.addEventListener('scroll', updateRect, true)
    window.addEventListener('resize', updateRect)
    return () => {
      window.removeEventListener('scroll', updateRect, true)
      window.removeEventListener('resize', updateRect)
    }
  }, [agendaImportDropdownOpen, loadingAgendaImportList])

  // Close duration dropdown on click outside
  useEffect(() => {
    if (!durationDropdownItemId) return
    const handleClick = (e) => {
      if (durationDropdownRef.current && !durationDropdownRef.current.contains(e.target)) {
        setDurationDropdownItemId(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [durationDropdownItemId])

  // Close meeting duration dropdown on click outside
  useEffect(() => {
    if (!meetingDurationDropdownOpen) return
    const handleClick = (e) => {
      if (meetingDurationDropdownRef.current && !meetingDurationDropdownRef.current.contains(e.target)) {
        setMeetingDurationDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [meetingDurationDropdownOpen])

  // Init scheduling timezone from user profile once user data is available
  useEffect(() => {
    if (user) {
      const tz = getOrganizerTimezone(user)
      if (tz) setSchedulingTimezone(tz)
    }
  }, [user])

  // Close timezone dropdown on click outside
  useEffect(() => {
    if (!tzDropdownOpen) return
    const handleClick = (e) => {
      if (tzDropdownRef.current && !tzDropdownRef.current.contains(e.target)) {
        setTzDropdownOpen(false)
        setTzSearch('')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [tzDropdownOpen])

  useEffect(() => {
    return () => {
      if (governanceInfoTooltipTimeoutRef.current) clearTimeout(governanceInfoTooltipTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (pendingParticipantsQueue.length > 1) {
      pendingParticipantsTotalRef.current = Math.max(
        pendingParticipantsTotalRef.current ?? 0,
        pendingParticipantsQueue.length
      )
    }
    if (pendingParticipantsQueue.length === 0) {
      pendingParticipantsTotalRef.current = null
    }
  }, [pendingParticipantsQueue.length])

  // End time = start + max(user duration, agenda total). Duration is user input only; we never auto-change it.
  useEffect(() => {
    const total = meeting.agenda.reduce((sum, item) => sum + (item.duration || 0), 0)
    const effectiveMinutes = Math.max(meeting.duration ?? 0, total)
    if (!meeting.startTime || effectiveMinutes <= 0) {
      setMeeting((prev) => (prev.endTime ? { ...prev, endTime: undefined } : prev))
      return
    }
    const endTime = new Date(new Date(meeting.startTime).getTime() + effectiveMinutes * 60 * 1000).toISOString()
    setMeeting((prev) => (prev.endTime === endTime ? prev : { ...prev, endTime }))
  }, [meeting.startTime, meeting.duration, meeting.agenda])

  // Schedule conflict detection — debounced check against existing meetings
  const { overlapping: conflictOverlapping, meetingBefore: conflictBefore, meetingAfter: conflictAfter, hasOverlap: hasScheduleConflict, loading: conflictsLoading } =
    useScheduleConflicts(meeting.startTime, meeting.endTime, meetingId)

  // Fetch meetings for import dropdown when opened or search changes
  useEffect(() => {
    if (!importDropdownOpen) return
    const timer = setTimeout(() => {
      setLoadingImportList(true)
      meetingsAPI
        .listForImport(importSearch)
        .then((res) => {
          const list = res?.data?.meetings ?? res?.meetings ?? []
          setImportMeetings(list)
        })
        .catch(() => setImportMeetings([]))
        .finally(() => setLoadingImportList(false))
    }, 200)
    return () => clearTimeout(timer)
  }, [importDropdownOpen, importSearch])

  // Fetch available meeting types on mount
  useEffect(() => {
    meetingsAPI.getPresets()
      .then(res => {
        const presets = res.data?.presets || res.presets || []
        setMeetingTypes(presets)
        if (presets.length > 0) {
          setSelectedMeetingType((prev) => (presets.some((p) => p.id === prev) ? prev : presets[0].id))
        }
      })
      .catch(() => {
        setMeetingTypes([])
      })
      .finally(() => setLoadingTypes(false))
  }, [])

  useEffect(() => {
    if (!user?.id) return
    authAPI.getKnownContacts()
      .then((res) => {
        const contacts = res?.contacts ?? []
        const map = new Map(contacts.map((c) => [c.email.toLowerCase(), c.display_name]))
        setKnownContactsMap(map)
      })
      .catch(() => setKnownContactsMap(new Map()))
  }, [user?.id])

  useEffect(() => {
    const pendingId = pendingScrollAbAgendaItemIdRef.current
    if (!pendingId) return
    if (!(meeting.agenda ?? []).some((it) => it.id === pendingId)) {
      pendingScrollAbAgendaItemIdRef.current = null
      return
    }
    pendingScrollAbAgendaItemIdRef.current = null
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const row = document.querySelector(abAgendaItemSelector(pendingId))
        if (!row) return
        row.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        const titleInput = row.querySelector('input[placeholder="Agenda item title"]')
        if (titleInput instanceof HTMLInputElement) titleInput.focus()
      })
    })
  }, [meeting.agenda])

  /**
   * Import meeting data: extract text only (title, description, participants, agenda).
   * Generates new IDs - no reference to source meeting is persisted on save.
   */
  const handleImportSelect = async (sourceMeetingId) => {
    setImportingMeeting(true)
    setImportDropdownOpen(false)
    try {
      const res = await meetingsAPI.get(sourceMeetingId)
      const data = res?.data ?? res
      if (!data) return

      const agendaList = data.agenda ?? []
      const participantList = withoutOrganizerParticipants(data.participants ?? [], user?.email)

      setMeeting((prev) => ({
        ...prev,
        title: decodeHtmlEntities(String(data.title ?? prev.title ?? '')),
        description: decodeHtmlEntities(String(data.description ?? prev.description ?? '')),
        agenda: agendaList.map((item, index) => ({
          id: `item_${Date.now()}_${index}`,
          title: decodeHtmlEntities(String(item.title ?? '')),
          duration: item.durationSec ? Math.round(item.durationSec / 60) : (item.duration ?? 5),
          order: index,
          details: decodeHtmlEntities(String(item.description ?? '')),
          detailsAsBullets: true,
        })),
        participants: participantList
          .filter((p) => p.email || p.displayName || p.name)
          .map((p, index) => ({
            id: `participant_${Date.now()}_${index}`,
            name: decodeHtmlEntities(String(p.displayName ?? p.name ?? p.email ?? '')),
            email: String(p.email ?? ''),
            role: p.role ?? 'participant',
          })),
      }))

      if (data.meetingTypeId) setSelectedMeetingType(data.meetingTypeId)
      setRecurrenceKind(data.isRecurring ? 'recurring' : 'one_off')
      setRecurringSeriesId(null) // Always start a fresh series when importing; do not copy source's recurringSeriesId
      if (data.recurrenceRule && typeof data.recurrenceRule === 'object') {
        const nr = data.recurrenceRule
        const isNeverEnds = nr.never_ends === true
        setRecurrenceRule((prev) => ({
          type: nr.type ?? prev.type,
          repeat_interval: nr.repeat_interval ?? prev.repeat_interval,
          weekly_days: nr.weekly_days ?? prev.weekly_days,
          monthly_day: nr.monthly_day ?? prev.monthly_day,
          end_times: isNeverEnds ? 0 : (nr.end_times ?? prev.end_times),
          end_date_time: isNeverEnds ? '' : (nr.end_date_time ?? prev.end_date_time),
          never_ends: isNeverEnds,
        }))
      }

      toast.success('Meeting imported', 'Content from the selected meeting has been loaded. Edit as needed before saving.')
    } catch {
      toast.error('Import failed', 'Could not load the meeting. Please try again.')
    } finally {
      setImportingMeeting(false)
      setImportSearch('')
    }
  }

  /**
   * Import agenda only from another meeting. Extracts agenda items as text; generates new IDs.
   */
  const handleImportAgendaSelect = async (sourceMeetingId) => {
    setAgendaImportingMeeting(true)
    setAgendaImportDropdownOpen(false)
    try {
      const res = await meetingsAPI.get(sourceMeetingId)
      const data = res?.data ?? res
      if (!data) return

      const agendaList = data.agenda ?? []

      setMeeting((prev) => ({
        ...prev,
        agenda: agendaList.map((item, index) => ({
          id: `item_${Date.now()}_${index}`,
          title: decodeHtmlEntities(String(item.title ?? '')),
          duration: item.durationSec ? Math.round(item.durationSec / 60) : (item.duration ?? 5),
          order: index,
          details: decodeHtmlEntities(String(item.description ?? '')),
          detailsAsBullets: true,
        })),
      }))

      toast.success('Agenda imported', `Loaded ${agendaList.length} agenda items from the selected meeting.`)
    } catch {
      toast.error('Import failed', 'Could not load the agenda. Please try again.')
    } finally {
      setAgendaImportingMeeting(false)
      setAgendaImportSearch('')
    }
  }

  const updateMeeting = (updates) => {
    setMeeting((prev) => ({ ...prev, ...updates }))
    setErrors((prev) => {
      const next = { ...prev }
      Object.keys(updates).forEach((key) => delete next[key])
      return next
    })
  }

  /** Persist audience immediately when editing an existing meeting so GET reloads cannot drop "Guests" back to team-only. */
  const setAudienceMode = (mode) => {
    updateMeeting({ audienceMode: mode })
    if (!meetingId) return
    meetingsAPI
      .update(meetingId, { audienceMode: mode })
      .catch(() => {
        toast.error('Could not save meeting type', 'Your choice is kept on this page — try Save to sync, or reload and try again.')
      })
  }

  const setMeetingDuration = (minutes) => {
    const clamped = minutes == null || minutes === '' ? undefined : Math.max(1, Math.min(180, Number(minutes)))
    updateMeeting({ duration: clamped })
  }

  const setStartTime = (value) => {
    updateMeeting({ startTime: value })
  }

  const addAgendaItem = () => {
    if (
      meeting.duration != null &&
      totalDuration + DEFAULT_NEW_AGENDA_ITEM_MINUTES > meeting.duration
    ) {
      toast.error(
        'No room for another topic',
        `A new item needs at least ${DEFAULT_NEW_AGENDA_ITEM_MINUTES} minutes. Increase the meeting duration or shorten existing topics.`
      )
      return
    }
    const newItem = {
      id: `item_${Date.now()}`,
      title: '',
      duration: DEFAULT_NEW_AGENDA_ITEM_MINUTES,
      order: meeting.agenda.length,
      details: '',
      detailsAsBullets: true,
    }
    pendingScrollAbAgendaItemIdRef.current = newItem.id
    updateMeeting({ agenda: [...meeting.agenda, newItem] })
  }

  const updateAgendaItem = (itemId, updates) => {
    const updatedAgenda = meeting.agenda.map((item) =>
      item.id === itemId ? { ...item, ...updates } : item
    )
    updateMeeting({ agenda: updatedAgenda })
  }

  const removeAgendaItem = (itemId) => {
    const updatedAgenda = meeting.agenda.filter((item) => item.id !== itemId)
    updateMeeting({ agenda: updatedAgenda })
  }

  const reorderAgenda = (newOrder) => {
    const updatedAgenda = newOrder.map((item, index) => ({
      ...item,
      order: index,
    }))
    updateMeeting({ agenda: updatedAgenda })
  }

  const normalizeParticipantEmail = (value) => {
    const trimmed = value.trim().toLowerCase()
    if (!trimmed) return ''
    if (trimmed.includes('@')) return trimmed
    return `${trimmed}@${userDomain}`
  }

  const parseParticipantInputForEmails = (input) => {
    if (!input || typeof input !== 'string') return []
    return input
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .map((s) => (s.includes('@') ? s : `${s}@${userDomain}`))
      .filter((s) => s.includes('@'))
  }

  const guessParticipantNameFromEmail = (email) => {
    const atIndex = email.indexOf('@')
    const localRaw = atIndex >= 0 ? email.slice(0, atIndex) : email
    if (!localRaw) return null

    // Strip +tags commonly used in email aliases
    const baseLocal = localRaw.split('+')[0] || ''
    const cleaned = baseLocal.replace(/[._-]+/g, ' ').trim()
    if (!cleaned) return null

    const tokens = cleaned
      .split(/\s+/)
      .map((t) => t.replace(/^\d+|\d+$/g, '')) // strip leading/trailing digits from each token
      .filter((t) => t && /[a-zA-Z]/.test(t))

    if (tokens.length === 0) {
      return null
    }

    const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()

    if (tokens.length >= 2) {
      // Prefer first + last to avoid including middle initials / dept codes
      const first = tokens[0]
      const last = tokens[tokens.length - 1]
      return `${capitalize(first)} ${capitalize(last)}`
    }

    const [only] = tokens
    if (!only || only.length < 2) {
      return null
    }

    // Single token: allow short, letter-only tokens (e.g. "raghu", "alex") as first names.
    // Reject long or digit-heavy tokens to avoid "Hi Alexanderpombo" or "Hi User123".
    if (only.length <= 12 && /^[a-zA-Z]+$/.test(only)) {
      return capitalize(only)
    }
    return null
  }

  const commitParticipant = (email, name) => {
    const em = (email ?? '').trim().toLowerCase()
    if (organizerEmailLower && em === organizerEmailLower) {
      toast.error('Cannot add yourself', 'You are the meeting organizer. Add guests only — they receive the invite email.')
      setParticipantInput('')
      return
    }
    const existingEmails = new Set(
      meeting.participants.map((item) => (item.email ?? '').toLowerCase()).filter(Boolean)
    )
    if (!email || existingEmails.has(email.toLowerCase())) {
      setParticipantInput('')
      return
    }

    const newParticipant = {
      id: `participant_${Date.now()}`,
      name: name || undefined,
      email,
      role: 'participant',
    }
    updateMeeting({ participants: [...meeting.participants, newParticipant] })
    setParticipantInput('')
    setErrors((e) => (e?.participants ? { ...e, participants: undefined } : e))
  }

  const addParticipant = (participant) => {
    const rawValue = participant?.email ?? participantInput
    const email = normalizeParticipantEmail(rawValue)
    if (!email) return

    // When the participant already has a known name (e.g., from directory),
    // trust it and add immediately without extra confirmation.
    if (participant?.name && user?.id) {
      authAPI.upsertKnownContact(email, participant.name).then(() => {
        setKnownContactsMap((prev) => new Map(prev).set(email.toLowerCase(), participant.name))
      }).catch(() => {})
      commitParticipant(email, participant.name)
      return
    }

    // Check persistent memory: if we've seen this email before with a confirmed name, add immediately.
    const knownName = knownContactsMap.get(email.toLowerCase())
    if (knownName) {
      commitParticipant(email, knownName)
      return
    }

    const guess = guessParticipantNameFromEmail(email)
    setPendingParticipantsQueue((prev) => {
      const next = [...prev, { email, guess }]
      if (prev.length === 0) {
        setPendingParticipantName(guess || '')
      }
      return next
    })
  }

  const removeParticipant = (email) => {
    updateMeeting({
      participants: meeting.participants.filter((participant) => participant.email !== email),
    })
  }

  /**
   * Handle team checkbox toggle.
   * On check: fetch team members (cached), add them as participants deduped and excluding the organizer.
   * On uncheck: remove participants that came exclusively from team-sourced additions (i.e. not manually
   * added and not still covered by another selected team).
   */
  const handleTeamToggle = async (teamId, checked) => {
    // Fetch and cache team members if not already loaded
    if (!teamMembersCacheRef.current[teamId]) {
      try {
        const res = await teamsAPI.get(teamId)
        const members = res?.data?.members ?? []
        teamMembersCacheRef.current[teamId] = members
      } catch {
        teamMembersCacheRef.current[teamId] = []
      }
    }

    if (checked) {
      // Add team members that aren't already participants and aren't the organizer
      const newTeamIds = [...selectedTeamIds, teamId]
      setSelectedTeamIds(newTeamIds)

      const teamMembers = teamMembersCacheRef.current[teamId] ?? []
      setMeeting((prev) => {
        const existingEmails = new Set(
          (prev.participants ?? []).map((p) => (p.email ?? '').toLowerCase()).filter(Boolean)
        )
        const toAdd = teamMembers
          .filter((m) => {
            const email = (m.email ?? '').toLowerCase().trim()
            return email && email !== organizerEmailLower && !existingEmails.has(email)
          })
          .map((m) => ({
            id: `participant_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            name: m.fullName || m.full_name || undefined,
            email: (m.email ?? '').toLowerCase().trim(),
            role: 'participant',
            _fromTeam: true,
          }))
        return { ...prev, participants: [...(prev.participants ?? []), ...toAdd] }
      })
    } else {
      // Remove this team from selected list
      const newTeamIds = selectedTeamIds.filter((id) => id !== teamId)
      setSelectedTeamIds(newTeamIds)

      // Compute the set of emails covered by the remaining selected teams
      const remainingTeamEmails = new Set(
        newTeamIds.flatMap((tid) =>
          (teamMembersCacheRef.current[tid] ?? []).map((m) => (m.email ?? '').toLowerCase().trim()).filter(Boolean)
        )
      )

      // Emails exclusively from the deselected team (not in any remaining team)
      const deselectedTeamEmails = new Set(
        (teamMembersCacheRef.current[teamId] ?? [])
          .map((m) => (m.email ?? '').toLowerCase().trim())
          .filter((e) => e && !remainingTeamEmails.has(e))
      )

      // Remove participants that came from a team selection (flagged _fromTeam) and are now uncovered
      setMeeting((prev) => ({
        ...prev,
        participants: (prev.participants ?? []).filter((p) => {
          const email = (p.email ?? '').toLowerCase().trim()
          return !(p._fromTeam && deselectedTeamEmails.has(email))
        }),
      }))
    }
    setErrors((e) => (e?.participants ? { ...e, participants: undefined } : e))
  }

  const addParticipantsFromInput = () => {
    const emails = parseParticipantInputForEmails(participantInput)
    if (emails.length > 0) {
      emails.forEach((email) => addParticipant({ email }))
      setParticipantInput('')
    } else {
      addParticipant({ email: participantInput })
      setParticipantInput('')
    }
  }

  const handleParticipantKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      addParticipantsFromInput()
    }
  }

  const currentPendingParticipant = pendingParticipantsQueue[0] ?? null

  const confirmPendingParticipant = () => {
    if (!currentPendingParticipant) return
    const email = currentPendingParticipant.email
    const explicitName = pendingParticipantName.trim()
    const fallbackGuess = currentPendingParticipant.guess || guessParticipantNameFromEmail(email) || null
    const finalName = explicitName || fallbackGuess || null
    if (finalName && user?.id) {
      authAPI.upsertKnownContact(email, finalName).then(() => {
        setKnownContactsMap((prev) => new Map(prev).set(email.toLowerCase(), finalName))
      }).catch(() => {})
    }
    commitParticipant(email, finalName)
    setPendingParticipantsQueue((prev) => {
      const next = prev.slice(1)
      setPendingParticipantName(next[0]?.guess ?? '')
      return next
    })
  }

  const cancelPendingParticipant = () => {
    setPendingParticipantsQueue((prev) => {
      const next = prev.slice(1)
      setPendingParticipantName(next[0]?.guess ?? '')
      return next
    })
  }

  const formatDateTimeLocal = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    if (effectiveSchedulingTimezone) {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: effectiveSchedulingTimezone,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(date)
        const get = (type) => parts.find((p) => p.type === type)?.value ?? '00'
        const h = get('hour') === '24' ? '00' : get('hour')
        return `${get('year')}-${get('month')}-${get('day')}T${h}:${get('minute')}`
      } catch { /* fall through */ }
    }
    const pad = (num) => String(num).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}`
  }

  const formatDateLocal = (value) => {
    if (!value) return ''
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const pad = (num) => String(num).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  const getRecurrenceRulePayload = () => {
    if (recurrenceKind !== 'recurring') return undefined
    const hasEnd =
      recurrenceRule.never_ends === true ||
      recurrenceRule.end_times > 0 ||
      (recurrenceRule.end_date_time && recurrenceRule.end_date_time.trim() !== '')
    if (!hasEnd) return undefined
    if (recurrenceRule.type === 2) {
      const days = (recurrenceRule.weekly_days || '').split(',').map((d) => d.trim()).filter(Boolean)
      if (days.length === 0) return undefined
      const valid = [...new Set(days.filter((d) => /^[1-7]$/.test(d)))].sort((a, b) => Number(a) - Number(b))
      if (valid.length === 0) return undefined
    }
    if (recurrenceRule.type === 3) {
      const md = Number(recurrenceRule.monthly_day)
      if (!Number.isInteger(md) || md < 1 || md > 31) return undefined
    }
    const payload = {
      type: recurrenceRule.type,
      repeat_interval: recurrenceRule.repeat_interval || 1,
    }
    if (recurrenceRule.type === 2 && recurrenceRule.weekly_days) {
      const days = (recurrenceRule.weekly_days || '').split(',').map((d) => d.trim()).filter((d) => /^[1-7]$/.test(d))
      if (days.length > 0) payload.weekly_days = [...new Set(days)].sort((a, b) => Number(a) - Number(b)).join(',')
    }
    if (recurrenceRule.type === 3) {
      const md = Number(recurrenceRule.monthly_day)
      if (Number.isInteger(md) && md >= 1 && md <= 31) payload.monthly_day = md
    }
    if (recurrenceRule.never_ends === true) {
      payload.never_ends = true
    } else {
      if (recurrenceRule.end_times > 0) payload.end_times = recurrenceRule.end_times
      if (recurrenceRule.end_date_time && recurrenceRule.end_date_time.trim() !== '') {
        payload.end_date_time = recurrenceRule.end_date_time.trim()
      }
    }
    return payload
  }

  const buildDraftRecord = (draftId) => ({
    id: String(draftId),
    title: meeting.title,
    description: meeting.description,
    agenda: meeting.agenda.map((item, index) => ({
      id: item.id ?? `item_${index}`,
      title: item.title ?? '',
      duration: item.duration ?? 5,
      order: item.order ?? index,
      details: item.details ?? '',
      detailsAsBullets: item.detailsAsBullets !== false,
    })),
    participants: meeting.participants,
    startTime: meeting.startTime,
    endTime: meeting.endTime,
    duration: meeting.duration,
    updatedAt: new Date().toISOString(),
    meetingType: selectedMeetingType,
    isRecurring: recurrenceKind === 'recurring',
    recurringSeriesId: recurrenceKind === 'recurring' ? recurringSeriesId : undefined,
    recurrenceRule: getRecurrenceRulePayload(),
  })

  const normalizedParticipantInput = participantInput.trim().toLowerCase()
  const participantSuggestions = (() => {
    if (!normalizedParticipantInput) return []
    const suggestions = []
    if (!normalizedParticipantInput.includes('@')) {
      const suggestedEmail = `${normalizedParticipantInput}@${userDomain}`
      if (!organizerEmailLower || suggestedEmail.toLowerCase() !== organizerEmailLower) {
        suggestions.push({
          name: knownContactsMap.get(suggestedEmail.toLowerCase()) || guessParticipantNameFromEmail(suggestedEmail),
          email: suggestedEmail,
          hint: 'Suggested',
        })
      }
    }
    participantDirectory.forEach((participant) => {
      const haystack = `${participant.name} ${participant.email}`.toLowerCase()
      if (haystack.includes(normalizedParticipantInput)) {
        suggestions.push({ ...participant, hint: 'Directory' })
      }
    })
    const existingEmails = new Set(meeting.participants.map((item) => item.email.toLowerCase()))
    return suggestions.filter((participant, index, self) => {
      const email = participant.email.toLowerCase()
      if (organizerEmailLower && email === organizerEmailLower) return false
      return (
        !existingEmails.has(email) &&
        self.findIndex((item) => item.email.toLowerCase() === email) === index
      )
    })
  })()

  const canEditWholeMeeting = isRecurringParent || isEditingOneOff

  const persistInternalAgendaForMeeting = useCallback(
    async (mid) => {
      const mode = meeting.audienceMode === 'external' ? 'external' : 'internal'
      const body =
        mode === 'external'
          ? buildInternalAgendaStateFromGoals(internalGoals, { authorUserId: user?.id ?? null })
          : emptyInternalAgendaState()
      await internalAgendaAPI.put(mid, body)
    },
    [internalGoals, meeting.audienceMode, user?.id]
  )

  const addInternalGoalRow = () => {
    const firstPublicId = meeting.agenda[0]?.id ?? ''
    setInternalGoals((prev) => [
      ...prev,
      {
        internalAgendaItemId: newInternalGoalId(),
        linkedPublicAgendaItemId: firstPublicId,
        desiredOutcome: 'gather_information',
        privateNotes: '',
        visibilityScope: 'shared_internal_team',
        authorUserId: null,
      },
    ])
  }

  const updateInternalGoalRow = (internalAgendaItemId, patch) => {
    setInternalGoals((prev) =>
      prev.map((g) => (g.internalAgendaItemId === internalAgendaItemId ? { ...g, ...patch } : g))
    )
  }

  const removeInternalGoalRow = (internalAgendaItemId) => {
    setInternalGoals((prev) => prev.filter((g) => g.internalAgendaItemId !== internalAgendaItemId))
  }

  const validate = (opts = {}) => {
    const { requireParticipants = false } = opts
    const newErrors = {}
    if (!meeting.title.trim()) newErrors.title = 'Title is required'
    if (recurrenceKind === null) newErrors.recurrence = 'Select One-off or Recurring'
    if (recurrenceKind === 'recurring') {
      if (recurrenceRule.type === 2) {
        const days = (recurrenceRule.weekly_days || '').split(',').map((d) => d.trim()).filter(Boolean)
        if (days.length === 0) {
          newErrors.recurrence = 'Select at least one day of the week for weekly meetings'
        }
      }
      if (recurrenceRule.type === 3) {
        const md = Number(recurrenceRule.monthly_day)
        if (!Number.isInteger(md) || md < 1 || md > 31) {
          newErrors.recurrence = 'Choose which day of the month the meeting repeats on (1–31)'
        }
      }
      if (!getRecurrenceRulePayload()) {
        newErrors.recurrence = newErrors.recurrence ?? 'Set how often and when the series ends (after N times or by date)'
      }
    }
    if (meeting.agenda.length === 0) newErrors.agenda = 'Add at least one agenda item'
    meeting.agenda.forEach((item, index) => {
      if (!item.title.trim()) {
        newErrors[`agenda_${index}`] = 'Item title is required'
      }
    })
    if (meeting.duration != null && meeting.duration < totalDuration) {
      newErrors.duration = 'Duration cannot be less than agenda total'
    }
    if (requireParticipants) {
      const participantsWithEmail = (meeting.participants ?? []).filter((p) => (p.email ?? '').trim())
      if (participantsWithEmail.length === 0) {
        newErrors.participants = 'Add at least one participant to create the meeting'
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }


  const handleSave = async () => {
    if (!validate()) return

    // Warn about scheduling conflicts before proceeding
    if (hasScheduleConflict) {
      const proceed = await askScheduleConflictConfirm()
      if (!proceed) return
    }

    setSaving(true)
    try {
      const agendaItems = meeting.agenda.map((item, index) => ({
        id: item.id,
        title: item.title,
        description: (item.details ?? '').trim() || undefined,
        durationSec: (item.duration || 5) * 60,
        order: index,
      }))
      const governanceConfig = {
        talkEquity: { enabled: meeting.governanceSettings?.talkEquity?.enabled !== false },
        interruptionPrevention: { enabled: meeting.governanceSettings?.interruptionPrevention?.enabled !== false },
        agendaEnforcement: { enabled: meeting.governanceSettings?.agendaEnforcement?.enabled !== false },
        autoEndMeeting: meeting.autoEndMeeting === true,
        demoMode: meeting.demoMode === true,
        requireOutputOwnership: meeting.requireOutputOwnership === true,
      }
      const participantsPayload = (meeting.participants ?? []).map((p) => ({
        email: p.email ?? '',
        name: p.name,
        displayName: p.name,
      }))

      if (meetingId) {
        const updatePayload = {
          title: meeting.title,
          description: meeting.description,
          agenda: agendaItems,
          participants: participantsPayload,
          governanceConfig,
          audienceMode: meeting.audienceMode === 'external' ? 'external' : 'internal',
          scheduledStartTime: meeting.startTime || undefined,
          scheduledEndTime: meeting.endTime || undefined,
          isRecurring: recurrenceKind === 'recurring',
          recurringSeriesId: recurrenceKind === 'recurring' ? (recurringSeriesId || undefined) : null,
          recurrenceRule: getRecurrenceRulePayload(),
          ...(selectedTeamIds.length > 0 ? { teamIds: selectedTeamIds } : {}),
        }
        // Only mark as draft if the meeting is already a draft — don't downgrade
        // a live or scheduled meeting back to draft when the user edits it.
        if (!canEditWholeMeeting && (meeting.isDraft !== false)) {
          updatePayload.isDraft = true
        }
        await meetingsAPI.update(meetingId, updatePayload)
        try {
          await persistInternalAgendaForMeeting(meetingId)
        } catch {
          toast.error(
            'Partial save',
            'Meeting updated, but internal goals could not be saved. Try Save again or open Meeting Details.'
          )
        }
        requestOnboardingVideosCheck()
        if (!canEditWholeMeeting) {
          upsertDraftMeeting(buildDraftRecord(meetingId))
        }
        const saveMsg = canEditWholeMeeting
          ? (isRecurringParent ? 'Agenda saved' : 'Meeting saved')
          : 'Draft saved'
        const saveDetail = canEditWholeMeeting
          ? (isRecurringParent ? 'Your recurring meeting agenda has been updated.' : 'Your meeting has been updated.')
          : 'Your meeting has been saved.'
        toast.success(saveMsg, saveDetail)
        navigate(canEditWholeMeeting ? `/meeting/${meetingId}` : '/dashboard', { replace: true })
      } else {
        const created = await meetingsAPI.create({
          title: meeting.title,
          description: meeting.description,
          meetingType: selectedMeetingType,
          agenda: agendaItems,
          participants: participantsPayload,
          governanceConfig,
          audienceMode: meeting.audienceMode === 'external' ? 'external' : 'internal',
          isDraft: true,
          scheduledStartTime: meeting.startTime || undefined,
          scheduledEndTime: meeting.endTime || undefined,
          isRecurring: recurrenceKind === 'recurring',
          recurringSeriesId: recurrenceKind === 'recurring' ? recurringSeriesId : undefined,
          recurrenceRule: getRecurrenceRulePayload(),
          ...(selectedTeamIds.length > 0 ? { teamIds: selectedTeamIds } : {}),
        })
        const resData = created?.data ?? created
        const id = resData?.meetingId ?? resData?.id
        const draftId = id ?? meeting.id ?? `draft_${Date.now()}`
        if (meeting.id && id && meeting.id !== id) {
          removeDraftMeeting(meeting.id)
        }
        if (id) {
          try {
            await persistInternalAgendaForMeeting(id)
          } catch {
            toast.error(
              'Draft saved',
              'Public agenda is stored; internal goals could not be saved. Edit this meeting again to retry.'
            )
          }
        }
        upsertDraftMeeting(buildDraftRecord(draftId))
        requestOnboardingVideosCheck()
        toast.success('Draft saved', 'Your meeting has been saved.')
        navigate('/dashboard', { replace: true })
      }
    } catch {
      toast.error('Save failed', 'Could not save the meeting. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAndSendUpdate = async () => {
    if (!validate() || !meetingId) return

    // Warn about scheduling conflicts before proceeding
    if (hasScheduleConflict) {
      const proceed = await askScheduleConflictConfirm()
      if (!proceed) return
    }

    setSaving(true)
    try {
      const agendaItems = meeting.agenda.map((item, index) => ({
        id: item.id,
        title: item.title,
        description: (item.details ?? '').trim() || undefined,
        durationSec: (item.duration || 5) * 60,
        order: index,
      }))
      const governanceConfig = {
        talkEquity: { enabled: meeting.governanceSettings?.talkEquity?.enabled !== false },
        interruptionPrevention: { enabled: meeting.governanceSettings?.interruptionPrevention?.enabled !== false },
        agendaEnforcement: { enabled: meeting.governanceSettings?.agendaEnforcement?.enabled !== false },
        autoEndMeeting: meeting.autoEndMeeting === true,
        demoMode: meeting.demoMode === true,
        requireOutputOwnership: meeting.requireOutputOwnership === true,
      }
      const participantsPayload = (meeting.participants ?? []).map((p) => ({
        id: p.id,
        email: p.email ?? '',
        name: p.name,
        displayName: p.name,
      }))
      await meetingsAPI.sendUpdate(meetingId, {
        title: meeting.title,
        description: meeting.description,
        agenda: agendaItems,
        participants: participantsPayload,
        governanceConfig,
        audienceMode: meeting.audienceMode === 'external' ? 'external' : 'internal',
        scheduledStartTime: meeting.startTime || undefined,
        scheduledEndTime: meeting.endTime || undefined,
        ...(isRecurringParent ? { isRecurring: true, recurringSeriesId: recurringSeriesId || undefined, recurrenceRule: getRecurrenceRulePayload() } : {}),
        ...(selectedTeamIds.length > 0 ? { teamIds: selectedTeamIds } : {}),
      })
      try {
        await persistInternalAgendaForMeeting(meetingId)
        toast.success('Update sent', 'Agenda saved and participants notified.')
      } catch {
        toast.error(
          'Update partially saved',
          'Participants were notified, but internal goals could not be saved. Open Edit Meeting to retry.'
        )
      }
      navigate(`/meeting/${meetingId}`, { replace: true })
    } catch {
      toast.error('Send update failed', 'Could not save or send. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Create an instant meeting:
   * - If Zoom is connected: create on the user's Zoom account via OAuth token (user is host).
   * - Otherwise: C++ bot creates and hosts the Zoom meeting.
   * Recall.ai bot joins for transcription in both cases.
   */
  const handleStartMeeting = async () => {
    if (!validate({ requireParticipants: true })) return

    setStarting(true)
    setCreatedMeeting(null)

    const agendaItemsForDb = meeting.agenda.map((item, index) => ({
      id: item.id,
      title: item.title,
      description: (item.details ?? '').trim() || undefined,
      durationSec: (item.duration || 5) * 60,
      order: index,
    }))
    const participantsForDb = (meeting.participants ?? []).map((p) => ({
      email: p.email ?? '',
      name: p.name,
      displayName: p.name,
    }))
    const governanceConfig = {
      talkEquity: { enabled: meeting.governanceSettings?.talkEquity?.enabled !== false },
      interruptionPrevention: { enabled: meeting.governanceSettings?.interruptionPrevention?.enabled !== false },
      agendaEnforcement: { enabled: meeting.governanceSettings?.agendaEnforcement?.enabled !== false },
      autoEndMeeting: meeting.autoEndMeeting === true,
      demoMode: meeting.demoMode === true,
      requireOutputOwnership: meeting.requireOutputOwnership === true,
    }

    const participantsPayload = Array.isArray(meeting.participants)
      ? meeting.participants.map((p) => ({ id: p?.id, name: p?.name, email: p?.email }))
      : undefined

    try {
      let response
      if (zoomConnected && meetingId) {
        // Converting existing draft: update draft with latest data, then convert to live
        await meetingsAPI.update(meetingId, {
          title: meeting.title || 'Rithmo Meeting',
          description: meeting.description,
          meetingTypeId: selectedMeetingType,
          agenda: agendaItemsForDb,
          participants: participantsForDb,
          governanceConfig,
          audienceMode: meeting.audienceMode === 'external' ? 'external' : 'internal',
          isDraft: true,
          scheduledStartTime: meeting.startTime || undefined,
          scheduledEndTime: meeting.endTime || undefined,
          isRecurring: recurrenceKind === 'recurring',
          recurringSeriesId: recurrenceKind === 'recurring' ? (recurringSeriesId || undefined) : null,
          recurrenceRule: getRecurrenceRulePayload(),
          ...(selectedTeamIds.length > 0 ? { teamIds: selectedTeamIds } : {}),
        })
        try {
          await persistInternalAgendaForMeeting(meetingId)
        } catch {
          toast.error(
            'Internal goals not saved',
            'Public agenda was updated; save internal goals from Edit Meeting before the live session if needed.'
          )
        }
        response = await meetingsAPI.convertDraftToLive(meetingId, {
          enableRecallBot: true,
          recallTranscriptionOnly: true,
          organizerTimezone: effectiveSchedulingTimezone || undefined,
        })
      } else if (zoomConnected) {
        // New meeting: single API call creates Zoom meeting + stores in DB
        response = await meetingsAPI.create({
          title: meeting.title || 'Rithmo Meeting',
          description: meeting.description,
          meetingType: selectedMeetingType,
          agenda: agendaItemsForDb,
          participants: participantsForDb,
          governanceConfig,
          audienceMode: meeting.audienceMode === 'external' ? 'external' : 'internal',
          isDraft: false,
          createWithZoom: true,
          enableRecallBot: true,
          recallTranscriptionOnly: true,
          scheduledStartTime: meeting.startTime || undefined,
          scheduledEndTime: meeting.endTime || undefined,
          isRecurring: recurrenceKind === 'recurring',
          recurringSeriesId: recurrenceKind === 'recurring' ? recurringSeriesId : undefined,
          recurrenceRule: getRecurrenceRulePayload(),
          organizerTimezone: effectiveSchedulingTimezone || undefined,
          ...(selectedTeamIds.length > 0 ? { teamIds: selectedTeamIds } : {}),
        })
      } else {
        // Zoom not connected: create draft only; live meetings require Zoom
        response = await meetingsAPI.create({
          title: meeting.title || 'Rithmo Meeting',
          description: meeting.description,
          meetingType: selectedMeetingType,
          agenda: agendaItemsForDb,
          participants: participantsForDb,
          governanceConfig,
          audienceMode: meeting.audienceMode === 'external' ? 'external' : 'internal',
          isDraft: true,
          createWithZoom: false,
          scheduledStartTime: meeting.startTime || undefined,
          scheduledEndTime: meeting.endTime || undefined,
          isRecurring: recurrenceKind === 'recurring',
          recurringSeriesId: recurrenceKind === 'recurring' ? recurringSeriesId : undefined,
          recurrenceRule: getRecurrenceRulePayload(),
          ...(selectedTeamIds.length > 0 ? { teamIds: selectedTeamIds } : {}),
        })
      }

      if (response && response.success === false) {
        throw new Error(response.error?.message || response.message || 'Failed to create meeting')
      }

      const raw = response?.data ?? response ?? {}
      const meetingData = raw.meeting ?? raw
      const joinUrl =
        meetingData?.joinUrl ??
        meetingData?.join_url ??
        meetingData?.meeting_url ??
        raw.joinUrl ??
        raw.join_url ??
        ''
      const meetingIdStr = String(
        meetingData?.meetingId ?? meetingData?.meetingNumber ?? meetingData?.id ?? raw.meetingId ?? raw.meetingNumber ?? ''
      )
      const isDraftOnly = raw.isDraft === true || (!joinUrl && raw.createdWith !== 'user_oauth')
      let internalGoalsPersistWarning = false
      let internalGoalsPersistMessage = ''
      if (meetingIdStr) {
        try {
          await persistInternalAgendaForMeeting(meetingIdStr)
        } catch (e) {
          internalGoalsPersistWarning = true
          internalGoalsPersistMessage = e instanceof Error ? e.message : String(e)
        }
      }
      if (!meetingIdStr && !joinUrl && !isDraftOnly) {
        throw new Error('Create succeeded but no meeting ID or join URL returned')
      }
      const rBot = raw.recallBot
      const cBot = raw.cppBot
      const recallJoined =
        rBot != null &&
        (typeof rBot === 'object'
          ? Boolean(rBot.joined === true || (rBot.id != null && rBot.id !== ''))
          : false)
      const cppJoined =
        cBot != null &&
        (typeof cBot === 'object'
          ? cBot.joined !== false && cBot.success !== false
          : true)
      const cppIsHost =
        cBot != null && (typeof cBot === 'object' ? cBot.isHost !== false : true)
      const addBotAfterYouJoin =
        cBot != null && (typeof cBot === 'object' ? cBot.addBotAfterYouJoin === true : false)
      const createdWithUserZoom = raw.createdWith === 'user_oauth'
      const dbMeetingId = zoomConnected ? meetingIdStr : null
      const isRecurringCreated = Boolean(raw?.isRecurring ?? (recurrenceKind === 'recurring'))
      setCreatedMeeting({
        meetingId: meetingIdStr || `draft_${Date.now()}`,
        dbMeetingId,
        isRecurring: isRecurringCreated,
        joinUrl: String(joinUrl),
        password: meetingData?.password != null && meetingData?.password !== '' ? String(meetingData.password) : null,
        topic: String(meetingData?.topic ?? meeting.title ?? 'Rithmo Meeting'),
        createdWithUserZoom: Boolean(createdWithUserZoom),
        draftOnly: isDraftOnly,
        cppBot: {
          isHost: Boolean(cppIsHost),
          joined: Boolean(cppJoined),
          addBotAfterYouJoin: Boolean(addBotAfterYouJoin) || createdWithUserZoom,
        },
        recallBot: {
          joined: Boolean(recallJoined),
        },
      })

      if (meetingId) removeDraftMeeting(String(meetingId))
      if (meeting?.id && String(meeting.id) !== String(meetingId ?? '')) {
        removeDraftMeeting(String(meeting.id))
      }

      if (internalGoalsPersistWarning) {
        const detail =
          internalGoalsPersistMessage && internalGoalsPersistMessage.length > 0 && internalGoalsPersistMessage.length < 280
            ? internalGoalsPersistMessage
            : 'Meeting was created, but internal goals could not be saved. Open Edit Meeting to retry.'
        toast.error('Partial save', detail)
      } else if (isDraftOnly) {
        toast.success('Draft saved', 'Connect Zoom to create a live meeting.')
      } else {
        toast.success(
          'Meeting Created!',
          'Your Rithmo meeting is ready. Click "Join Meeting" to enter.'
        )
      }

      requestOnboardingVideosCheck()
    } catch (error) {
      if (error.message?.includes('zoom_reauth_required') || error.message?.includes('Zoom connection has expired')) {
        toast.error('Zoom Disconnected', 'Your Zoom connection has expired. Reconnecting...')
        if (connectZoomUrl) {
          setTimeout(() => { window.location.href = connectZoomUrl }, 1500)
        }
      } else {
        toast.error('Creation Failed', error.message || 'Failed to create the meeting. Please try again.')
      }
      setCreatedMeeting(null)
    } finally {
      setStarting(false)
    }
  }

  /**
   * Start the meeting with the bot as host (if backend supports it), then open join URL.
   * When the user created the meeting with Zoom OAuth, we call start-existing so the bot
   * starts the meeting as host; then the user joins via the link.
   * For recurring: always create child occurrence first (startNextOccurrence), then start that.
   */
  const handleJoinMeeting = async () => {
    if (!createdMeeting?.joinUrl) return
    setJoiningMeeting(true)
    let effectiveMeetingId = createdMeeting.meetingId
    let joinUrl = createdMeeting.joinUrl
    let password = createdMeeting.password
    const wasRecurring = createdMeeting.isRecurring
    let zoomOk = false

    const revertStartingStatus = async () => {
      try {
        await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'created' })
      } catch (e) {
        console.warn('Could not revert meeting status to created after failed start', e)
      }
    }

    try {
      if (createdMeeting.meetingId) {
        if (wasRecurring) {
          const nextRes = await meetingsAPI.startNextOccurrence(createdMeeting.meetingId)
          effectiveMeetingId = nextRes?.data?.meetingId ?? nextRes?.meetingId ?? createdMeeting.meetingId
          if (nextRes?.data?.joinUrl ?? nextRes?.joinUrl) joinUrl = nextRes?.data?.joinUrl ?? nextRes?.joinUrl
          if ((nextRes?.data?.password ?? nextRes?.password) != null) password = nextRes?.data?.password ?? nextRes?.password
        }

        try {
          await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'starting' })
        } catch (e) {
          toast.error(
            e?.message ?? 'Could not reserve meeting start',
            'Please try again. If this keeps happening, refresh the page.'
          )
          setJoiningMeeting(false)
          return
        }

        // Try without `force` first. If the backend detects another live Zoom call on this
        // account it returns 409 CONCURRENT_ZOOM_CALL — confirm with the user before retrying
        // with `force: true`, which ends the other call.
        const callStart = (force) =>
          zoomAPI.startExistingMeetingForUser({
            meetingId: effectiveMeetingId,
            password,
            force,
          })
        try {
          let res
          try {
            res = await callStart(false)
          } catch (firstErr) {
            if (firstErr?.status === 409 && firstErr?.code === 'CONCURRENT_ZOOM_CALL') {
              const confirmed = await askConcurrentZoomConfirm()
              if (!confirmed) {
                if (wasRecurring && effectiveMeetingId !== createdMeeting.meetingId) {
                  try {
                    await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
                  } catch (cleanupErr) {
                    console.warn('Could not cancel orphaned occurrence', cleanupErr)
                  }
                }
                await revertStartingStatus()
                setJoiningMeeting(false)
                return
              }
              res = await callStart(true)
            } else if (firstErr?.status === 409 && firstErr?.code === 'CONCURRENT_ZOOM_CALL_DETECTED') {
              // The bot tried but got stuck in CONNECTING — surfaced after the fact because
              // the proactive detection couldn't run.
              if (wasRecurring && effectiveMeetingId !== createdMeeting.meetingId) {
                try {
                  await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
                } catch (cleanupErr) {
                  console.warn('Could not cancel orphaned occurrence', cleanupErr)
                }
              }
              await revertStartingStatus()
              toast.error(
                'Already in another Zoom call',
                'Please leave your other Zoom meeting and try again.'
              )
              setJoiningMeeting(false)
              return
            } else {
              throw firstErr
            }
          }
          if (res?.success) {
            toast.success('Meeting started', res?.message ?? 'Opening join link…')
            zoomOk = true
            setCreatedMeeting((prev) =>
              prev
                ? {
                    ...prev,
                    meetingId: effectiveMeetingId,
                    joinUrl,
                    password,
                    cppBot: { ...prev.cppBot, joined: true, isHost: true, addBotAfterYouJoin: false },
                  }
                : prev
            )
          }
        } catch (zoomErr) {
          // Zoom start failed — cancel any orphaned child occurrence so the parent
          // doesn't get stuck showing "meeting in progress", then revert the
          // effective meeting back to 'created' so the user can retry.
          if (wasRecurring && effectiveMeetingId !== createdMeeting.meetingId) {
            try {
              await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
            } catch (cleanupErr) {
              console.warn('Could not cancel orphaned occurrence', cleanupErr)
            }
          }
          await revertStartingStatus()
          toast.error(zoomErr?.message ?? 'Could not start meeting with bot; you can still join via the link.')
          setJoiningMeeting(false)
          return
        }
        if (!zoomOk) {
          await revertStartingStatus()
          setJoiningMeeting(false)
          return
        }
      }
    } catch (err) {
      toast.error(err?.message ?? 'Could not start meeting with bot; you can still join via the link.')
      setJoiningMeeting(false)
      return
    } finally {
      setJoiningMeeting(false)
    }
    // Backend startExistingMeetingForUser already set DB status to 'starting'.
    // The lifecycle coordinator will transition to 'live' when auto-start fires.
    // Do NOT prematurely patch to 'live' here — that causes a flicker on the dashboard.
    // Capture the values we need for navigation before the setTimeout so we
    // don't read stale React state inside the callback (setCreatedMeeting above
    // is async and the closure would see the old snapshot).
    const navCreatedWithUserZoom = createdMeeting.createdWithUserZoom
    // Bot joined = true if startExistingMeetingForUser succeeded (res?.success)
    const navCppBotJoined = createdMeeting.cppBot?.joined === true || false
    const navAddBotAfterYouJoin = createdMeeting.cppBot?.addBotAfterYouJoin ?? false

    // After start-existing has been awaited, open meeting in new tab and navigate to meeting page
    window.open(joinUrl, '_blank')
    setTimeout(() => {
      navigate(`/meeting/${effectiveMeetingId}`, {
        state: {
          joinUrl,
          password,
          createdWithUserZoom: navCreatedWithUserZoom,
          cppBotJoined: navCppBotJoined,
          addBotAfterYouJoin: navAddBotAfterYouJoin,
        },
      })
    }, 1000)
  }

  const handleVoiceBuilder = () => {
    if (meetingId) {
      navigate(`/agenda/voice/${meetingId}`)
    } else {
      navigate('/agenda/voice')
    }
  }

  const handleInstantMeeting = () => {
    navigate(QUICK_START_ROUTE)
  }

  if (loadingMeeting && meetingId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
          <div className="flex items-center justify-center h-[60vh]">
            <Loader2 className="w-12 h-12 animate-spin text-primary-500" />
          </div>
        </main>
      </div>
    )
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <MeetingSetupOverlay
        visible={starting}
        title="Setting Up Rithmo"
        subtitle="Creating your meeting..."
      />
      <MeetingSetupOverlay
        visible={!starting && joiningMeeting}
        title="Setting Up Rithmo"
        subtitle="Starting meeting & connecting bot..."
      />

      <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8"
        >
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {meetingId ? 'Edit Meeting' : 'Create New Meeting'}
            </h1>
            <p className="text-gray-500">Build your meeting agenda</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 justify-start md:justify-end">
            <button
              onClick={handleInstantMeeting}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700 transition-colors hover:border-emerald-300 hover:bg-emerald-100"
            >
              <Sparkles className="w-4 h-4" />
              Instant Meeting
            </button>
            <button
              onClick={handleVoiceBuilder}
              className="btn-primary flex items-center gap-2"
            >
              <Mic className="w-4 h-4" />
              Voice Builder
            </button>
            <button
              onClick={() => navigate(meetingId ? `/meeting/${meetingId}` : '/dashboard')}
              className="btn-ghost"
            >
              Cancel
            </button>
            {canEditWholeMeeting ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || starting || loadingMeeting}
                  className="btn-secondary flex items-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleSaveAndSendUpdate}
                  disabled={saving || starting || loadingMeeting}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  {saving ? 'Sending...' : 'Save and send update to participants'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || starting || loadingMeeting}
                  className="btn-secondary flex items-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                {!createdMeeting && !zoomConnected ? (
                  <div className="flex flex-col items-end gap-1.5">
                    <button
                      disabled
                      className="flex items-center gap-2 bg-gray-300 text-gray-500 cursor-not-allowed opacity-75 rounded-lg px-4 py-2"
                    >
                      <Play className="w-5 h-5" />
                      Create Meeting
                    </button>
                    <div className="flex items-center gap-1.5 text-xs text-amber-700">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <span>Zoom is not connected. <a href={connectZoomUrl || '#'} className="font-semibold underline hover:text-amber-900">Connect Zoom</a> to create meetings.</span>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={
                      createdMeeting
                        ? handleJoinMeeting
                        : isFormValidForCreate
                          ? handleStartMeeting
                          : (e) => {
                              e.preventDefault()
                              scrollToFirstIncomplete()
                            }
                    }
                    disabled={saving || starting || loadingMeeting}
                    className={`flex items-center gap-2 ${
                      !createdMeeting && !isFormValidForCreate
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-75 rounded-lg px-4 py-2'
                        : 'btn-primary'
                    }`}
                  >
                    {starting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : createdMeeting ? (
                      <Video className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5" />
                    )}
                    {starting ? 'Creating...' : createdMeeting ? 'Join Meeting' : 'Create Meeting'}
                  </button>
                )}
              </>
            )}
          </div>
        </motion.div>

        {/* Import Meeting - searchable dropdown (shown for create and edit) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6 overflow-visible"
        >
          <div className="glass rounded-2xl p-6 overflow-visible">
            <div className="flex items-center gap-2 mb-3">
              <FileDown className="w-5 h-5 text-primary-600" />
              <h2 className="text-base font-semibold text-gray-900">Import from existing meeting</h2>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Load title, description, participants, and agenda from a previous meeting.
            </p>
            <div className="relative w-full">
              <input
                ref={importInputRef}
                type="text"
                value={importSearch}
                onChange={(e) => setImportSearch(e.target.value)}
                onFocus={() => {
                  setImportDropdownOpen(true)
                  if (importInputRef.current) {
                    setImportDropdownRect(importInputRef.current.getBoundingClientRect())
                  }
                }}
                onBlur={() => setTimeout(() => setImportDropdownOpen(false), 150)}
                placeholder="Search meeting by title..."
                className="input-field pr-10"
                disabled={importingMeeting}
              />
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              {importDropdownOpen && importDropdownRect && createPortal(
                <div
                  className="fixed z-[9999] rounded-xl border border-gray-200 bg-white shadow-xl max-h-60 overflow-auto"
                  style={{
                    left: importDropdownRect.left,
                    top: importDropdownRect.bottom + 4,
                    width: importDropdownRect.width,
                    minWidth: 280,
                  }}
                >
                  {loadingImportList ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                    </div>
                  ) : (() => {
                    const filtered = importMeetings.filter((m) => !meetingId || m.meetingId !== meetingId)
                    return filtered.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-gray-500">No meetings found</p>
                    ) : (
                    filtered.map((m) => (
                      <button
                        key={m.meetingId}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleImportSelect(m.meetingId)
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100 last:border-0"
                      >
                        <FileDown className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="truncate text-gray-800">
                          {decodeHtmlEntities(m.title ?? '') || 'Untitled meeting'}
                        </span>
                      </button>
                    ))
                  )
                  })()}
                </div>,
                document.body
              )}
            </div>
          </div>
        </motion.div>

        {meetingId && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 }}
            className="mb-6"
          >
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-primary-600" />
                <h2 className="text-base font-semibold text-gray-900">Meeting prep materials</h2>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Upload meeting materials here. Stored for your organization; generating agenda ideas runs on our
                servers from prep metadata (no document content sent to third-party AI — ideas may stay empty until the
                prep pipeline is rebuilt).
              </p>
              <DocumentIntelligencePanel
                meetingId={meetingId}
                listScope={isRecurringParent ? 'series' : 'effective'}
                title={isRecurringParent ? 'Meeting documents' : 'Meeting materials'}
                showAgendaSuggestions={docIntelShowSuggestions}
                onAfterApply={reloadAgendaFromServer}
              />
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-1 gap-8">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            {/* Meeting Type Selector */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Layers className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">Meeting Type</h2>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <p className="text-sm text-gray-500">
                  Select a meeting type to automatically configure starting governance settings.
                </p>
                <div
                  className="relative shrink-0"
                  onMouseEnter={() => {
                    governanceInfoTooltipTimeoutRef.current = setTimeout(() => setGovernanceInfoTooltip(true), 100)
                  }}
                  onMouseLeave={() => {
                    if (governanceInfoTooltipTimeoutRef.current) clearTimeout(governanceInfoTooltipTimeoutRef.current)
                    governanceInfoTooltipTimeoutRef.current = null
                    setGovernanceInfoTooltip(false)
                  }}
                >
                  <span className="inline-flex text-gray-400 hover:text-gray-600 cursor-help">
                    <Info className="w-4 h-4" />
                  </span>
                  {governanceInfoTooltip && (
                    <div
                      className="absolute bottom-full left-0 mb-1.5 z-30 w-56 rounded-lg bg-gray-800 text-white text-xs px-3 py-2 shadow-lg pointer-events-none"
                      role="tooltip"
                    >
                      Governance settings autonomously evolve after any unique meeting.
                    </div>
                  )}
                </div>
              </div>

              {loadingTypes ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[1fr,minmax(260px,320px)] gap-6 lg:gap-8 items-start">
                  {/* Left: meeting type cards in 2 columns */}
                  <div className="grid grid-cols-2 gap-3">
                    {meetingTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setSelectedMeetingType(type.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selectedMeetingType === type.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <p className="font-medium text-gray-900 text-sm">{formatMeetingTypeDisplayName(type.name)}</p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{decodeHtmlEntities(type.description)}</p>
                        {selectedMeetingType === type.id && (
                          <div className="flex items-center gap-1 mt-2 text-primary-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs font-medium">Selected</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Right: configuration preview above the fold */}
                  {(() => {
                    const selectedType = meetingTypes.find(t => t.id === selectedMeetingType)
                    const getStrictnessColor = (value) => {
                      const colors = {
                        'Off': 'bg-gray-100 text-gray-600',
                        'Low': 'bg-blue-100 text-blue-700',
                        'Moderate': 'bg-yellow-100 text-yellow-700',
                        'Medium': 'bg-yellow-100 text-yellow-700',
                        'High': 'bg-orange-100 text-orange-700',
                        'VeryHigh': 'bg-red-100 text-red-700',
                        'Strict': 'bg-red-100 text-red-700',
                        'Gentle': 'bg-green-100 text-green-700',
                      }
                      return colors[value] || 'bg-gray-100 text-gray-600'
                    }
                    const getDisplayValue = (value) => {
                      const labels = { 'VeryHigh': 'Very High', 'Off': 'Off' }
                      return labels[value] || value
                    }
                    if (!selectedType) {
                      return (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-4 text-center">
                          <p className="text-sm text-gray-500">Select a type to see configuration</p>
                        </div>
                      )
                    }
                    const config = selectedType.config || {}
                    return (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        key={selectedMeetingType}
                        className="p-4 rounded-xl bg-gradient-to-r from-primary-50 to-purple-50 border border-primary-100 lg:sticky lg:top-4"
                      >
                        <div className="mb-3">
                          <span className="text-sm font-medium text-gray-900">
                            Configuration for {formatMeetingTypeDisplayName(selectedType.name)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex flex-col p-2 rounded-lg bg-white/60">
                            <div className="h-10 flex items-center justify-center">
                              <p className="text-[10px] uppercase tracking-wide text-gray-500 leading-tight line-clamp-2 text-center">Agenda Enforcement</p>
                            </div>
                            <div className="h-8 flex items-center justify-center shrink-0">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStrictnessColor(config.agenda)}`}>
                                {getDisplayValue(config.agenda)}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col p-2 rounded-lg bg-white/60">
                            <div className="h-10 flex items-center justify-center">
                              <p className="text-[10px] uppercase tracking-wide text-gray-500 leading-tight line-clamp-2 text-center">Time Enforcement</p>
                            </div>
                            <div className="h-8 flex items-center justify-center shrink-0">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStrictnessColor(config.time)}`}>
                                {getDisplayValue(config.time)}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col p-2 rounded-lg bg-white/60">
                            <div className="h-10 flex items-center justify-center">
                              <p className="text-[10px] uppercase tracking-wide text-gray-500 leading-tight line-clamp-2 text-center">Interruption Detection</p>
                            </div>
                            <div className="h-8 flex items-center justify-center shrink-0">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStrictnessColor(config.interrupt)}`}>
                                {getDisplayValue(config.interrupt)}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col p-2 rounded-lg bg-white/60">
                            <div className="h-10 flex items-center justify-center">
                              <p className="text-[10px] uppercase tracking-wide text-gray-500 leading-tight line-clamp-2 text-center">Talk Equity</p>
                            </div>
                            <div className="h-8 flex items-center justify-center shrink-0">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStrictnessColor(config.talkEquity)}`}>
                                {getDisplayValue(config.talkEquity)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )
                  })()}
                </div>
              )}
            </div>

            {/* Meeting Details */}
            <div ref={meetingDetailsSectionRef} className="glass rounded-2xl p-6 scroll-mt-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Meeting Details
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-500 mb-2">
                    Meeting Title *
                  </label>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={decodeHtmlEntities(meeting.title ?? '')}
                    onChange={(e) => updateMeeting({ title: e.target.value })}
                    placeholder="e.g., Weekly Team Standup"
                    className={`input-field ${errors.title ? 'border-red-300' : ''}`}
                  />
                  {errors.title && (
                    <p className="text-sm text-red-500 mt-1">{errors.title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-500 mb-2">Who is this meeting for?</label>
                  <div className="flex rounded-xl border-2 border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setAudienceMode('internal')}
                      className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${
                        (meeting.audienceMode ?? 'internal') !== 'external'
                          ? 'bg-primary-500 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Team only
                    </button>
                    <button
                      type="button"
                      onClick={() => setAudienceMode('external')}
                      className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${
                        meeting.audienceMode === 'external'
                          ? 'bg-primary-500 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Guests outside your team
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {'"Team only" uses one shared agenda. "Guests outside your team" adds a private internal goals track alongside the guest-visible agenda.'}
                    {meetingId ? ' Your choice is saved to the meeting when you switch.' : ' Save the meeting to store this setting.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-gray-500 mb-2">
                    Recurrence *
                  </label>
                  <div className="flex rounded-xl border-2 border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => { setRecurrenceKind('one_off'); setErrors((e) => ({ ...e, recurrence: undefined })) }}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-all ${
                        recurrenceKind === 'one_off'
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-600 hover:bg-gray-50 border-transparent'
                      }`}
                    >
                      <CalendarDays className="w-4 h-4" />
                      One-off
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRecurrenceKind('recurring'); setErrors((e) => ({ ...e, recurrence: undefined })) }}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-all ${
                        recurrenceKind === 'recurring'
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-600 hover:bg-gray-50 border-transparent'
                      }`}
                    >
                      <Repeat className="w-4 h-4" />
                      Recurring
                    </button>
                  </div>
                  {errors.recurrence && (
                    <p className="text-sm text-red-500 mt-1">{errors.recurrence}</p>
                  )}
                  {recurrenceKind === 'recurring' && (
                    <div className="mt-3 p-4 rounded-xl border border-gray-200 bg-gray-50/50 space-y-3">
                      <p className="text-sm font-medium text-gray-700">Recurrence details</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Repeats</label>
                          <select
                            value={recurrenceRule.type}
                            onChange={(e) => {
                              const t = Number(e.target.value)
                              setRecurrenceRule((r) => {
                                if (t === 1) return { ...r, type: t, weekly_days: '', monthly_day: '' }
                                if (t === 2) return { ...r, type: t, monthly_day: '' }
                                if (t === 3) {
                                  let md = Number(r.monthly_day)
                                  if (!Number.isInteger(md) || md < 1 || md > 31) {
                                    const st = meeting.startTime
                                    const d = st ? new Date(st) : null
                                    md =
                                      d && !Number.isNaN(d.getTime()) ? d.getDate() : new Date().getDate()
                                  }
                                  return { ...r, type: t, weekly_days: '', monthly_day: md }
                                }
                                return { ...r, type: t }
                              })
                            }}
                            className="input-field text-sm"
                          >
                            <option value={1}>Daily</option>
                            <option value={2}>Weekly</option>
                            <option value={3}>Monthly</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Every (interval)</label>
                          <input
                            type="number"
                            min={1}
                            max={15}
                            value={recurrenceRule.repeat_interval}
                            onChange={(e) => setRecurrenceRule((r) => ({ ...r, repeat_interval: Math.max(1, Math.min(15, parseInt(e.target.value, 10) || 1)) }))}
                            className="input-field text-sm"
                          />
                        </div>
                        {recurrenceRule.type === 2 && (
                          <div className="sm:col-span-2 space-y-2">
                            <label className="block text-xs text-gray-500 mb-0">Days of week</label>
                            <div className="flex flex-wrap gap-2 items-center">
                              {[
                                { n: 1, label: 'Sun' },
                                { n: 2, label: 'Mon' },
                                { n: 3, label: 'Tue' },
                                { n: 4, label: 'Wed' },
                                { n: 5, label: 'Thu' },
                                { n: 6, label: 'Fri' },
                                { n: 7, label: 'Sat' },
                              ].map(({ n, label }) => {
                                const current = (recurrenceRule.weekly_days || '').split(',').map((d) => d.trim()).filter(Boolean)
                                const checked = current.includes(String(n))
                                return (
                                  <label
                                    key={n}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                                      checked ? 'bg-primary-50 border-primary-500 text-primary-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const next = e.target.checked
                                          ? [...current.filter((d) => d !== String(n)), String(n)].sort((a, b) => Number(a) - Number(b))
                                          : current.filter((d) => d !== String(n))
                                        setRecurrenceRule((r) => ({ ...r, weekly_days: next.join(',') }))
                                      }}
                                      className="sr-only"
                                    />
                                    <span className="text-sm font-medium">{label}</span>
                                  </label>
                                )
                              })}
                              <button
                                type="button"
                                onClick={() =>
                                  setRecurrenceRule((r) => ({
                                    ...r,
                                    weekly_days: '2,3,4,5,6',
                                  }))
                                }
                                className="inline-flex items-center px-3 py-1.5 rounded-lg border-2 border-gray-300 bg-white text-sm font-medium text-gray-700 hover:border-primary-500 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                              >
                                Weekdays (Mon–Fri)
                              </button>
                            </div>
                          </div>
                        )}
                        {recurrenceRule.type === 3 && (
                          <div className="sm:col-span-2 space-y-2">
                            <label className="block text-xs text-gray-500 mb-0">Day of month</label>
                            <p className="text-xs text-gray-500">Same calendar date each month (Zoom monthly series).</p>
                            <select
                              value={Number(recurrenceRule.monthly_day)}
                              onChange={(e) =>
                                setRecurrenceRule((r) => ({
                                  ...r,
                                  monthly_day: Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)),
                                }))
                              }
                              className="input-field text-sm max-w-[12rem]"
                            >
                              {AGENDA_MONTH_DAY_OPTIONS.map((day) => (
                                <option key={day} value={day}>
                                  {ordinalCalendarDayLabel(day)}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">End after (occurrences)</label>
                          <input
                            type="number"
                            min={1}
                            max={60}
                            value={recurrenceRule.end_times || ''}
                            disabled={recurrenceRule.never_ends === true}
                            onChange={(e) => {
                              const v = e.target.value === '' ? 0 : Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 1))
                              setRecurrenceRule((r) => ({
                                ...r,
                                end_times: v,
                                end_date_time: v ? r.end_date_time : r.end_date_time,
                                never_ends: false,
                              }))
                            }}
                            className="input-field text-sm disabled:bg-gray-100 disabled:text-gray-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            <Calendar className="w-3.5 h-3.5 inline mr-1" />
                            Or end by date
                          </label>
                          <input
                            type="date"
                            value={
                              // Display the stored date portion directly — avoid UTC→local conversion
                              // by extracting the YYYY-MM-DD prefix if it's a full timestamp.
                              recurrenceRule.end_date_time
                                ? (recurrenceRule.end_date_time.slice(0, 10) || '')
                                : ''
                            }
                            disabled={recurrenceRule.never_ends === true}
                            onChange={(e) => {
                              const val = e.target.value
                              if (!val) {
                                setRecurrenceRule((r) => ({ ...r, end_date_time: '', never_ends: false }))
                                return
                              }
                              // Store as a plain date string (YYYY-MM-DD) so there is no
                              // UTC/local timezone shift when displaying or sending to the server.
                              setRecurrenceRule((r) => ({
                                ...r,
                                end_date_time: val,
                                never_ends: false,
                              }))
                            }}
                            className="input-field text-sm disabled:bg-gray-100 disabled:text-gray-400"
                          />
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-2 pt-1">
                          <input
                            id="recurrence-never-ends"
                            type="checkbox"
                            checked={recurrenceRule.never_ends === true}
                            onChange={(e) =>
                              setRecurrenceRule((r) => ({
                                ...r,
                                never_ends: e.target.checked,
                                end_times: e.target.checked ? 0 : r.end_times,
                                end_date_time: e.target.checked ? '' : r.end_date_time,
                              }))
                            }
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <label
                            htmlFor="recurrence-never-ends"
                            className="text-xs text-gray-600"
                          >
                            Never ends
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {teamsList.length > 0 && (
                  <div>
                    <label className="block text-sm text-gray-500 mb-2">
                      Teams (optional)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Share this meeting with selected teams. Only members of those teams will see it in their list.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {teamsList.map((team) => {
                        const checked = selectedTeamIds.includes(team.id)
                        return (
                          <label
                            key={team.id}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                              checked ? 'bg-primary-50 border-primary-500 text-primary-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => handleTeamToggle(team.id, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm font-medium">{team.name}</span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )}

                <div ref={participantsSectionRef} className="scroll-mt-4">
                  <label className="block text-sm text-gray-500 mb-2">
                    Participants
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    You are the host. Add guest emails only — do not add your own address (you will not appear here).
                  </p>
                  <div className={`rounded-xl border bg-white p-4 space-y-3 ${errors.participants ? 'border-red-300' : 'border-gray-200'}`}>
                    {meeting.participants.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {meeting.participants.map((participant) => (
                          <span
                            key={participant.email}
                            className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
                          >
                            {participant.email}
                            <button
                              type="button"
                              onClick={() => removeParticipant(participant.email)}
                              className="text-gray-400 hover:text-gray-600"
                              aria-label={`Remove ${participant.email}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">No participants added yet.</p>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={participantInput}
                          onChange={(event) => setParticipantInput(event.target.value)}
                          onKeyDown={handleParticipantKeyDown}
                          placeholder="Add by name or email"
                          className="input-field"
                          autoComplete="off"
                        />
                        {participantSuggestions.length > 0 && (
                          <div className="absolute left-0 right-0 top-full z-10 mt-2 rounded-xl border border-gray-200 bg-white shadow-lg">
                            {participantSuggestions.map((participant) => (
                              <button
                                key={participant.email}
                                type="button"
                                onClick={() => addParticipant(participant)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                              >
                                <span className="text-gray-700">{participant.email}</span>
                                {participant.hint && (
                                  <span className="text-xs text-gray-400">{participant.hint}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={addParticipantsFromInput}
                        className="btn-secondary"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  {errors.participants && (
                    <p className="text-sm text-red-500 mt-1">{errors.participants}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Type a name (e.g., tim) to auto-suggest tim@{userDomain}.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 transition-colors">
                  <label
                    className="block text-sm text-gray-500 mb-2"
                    htmlFor="agenda-builder-scheduled-start"
                  >
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Scheduled Date & Time (optional)
                  </label>
                  {meeting.startTime && (() => {
                    const d = parseISO(meeting.startTime)
                    return isValid(d) ? (
                      <p className="text-xs font-medium text-primary-600 mb-2">
                        Selected: {effectiveSchedulingTimezone ? formatInTimezone(meeting.startTime, effectiveSchedulingTimezone) : format(d, 'EEE, MMM d • h:mm a')}
                      </p>
                    ) : null
                  })()}
                  {/* Invisible full-bleed input so the entire card (not only the small calendar control) is clickable. */}
                  <div className="relative mt-1 min-h-[3.25rem] rounded-xl border border-gray-200 bg-white transition-colors hover:border-primary-300 hover:bg-primary-50/40 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-500/20">
                    <div
                      className="pointer-events-none flex min-h-[3.25rem] w-full items-center justify-between gap-3 px-4 py-3"
                      aria-hidden
                    >
                      <span
                        className={
                          meeting.startTime && isValid(parseISO(meeting.startTime))
                            ? 'min-w-0 text-sm text-gray-800'
                            : 'min-w-0 text-sm text-gray-400'
                        }
                      >
                        {(() => {
                          if (!meeting.startTime) return 'Select date and time'
                          const d = parseISO(meeting.startTime)
                          if (!isValid(d)) return 'Select date and time'
                          try {
                            if (effectiveSchedulingTimezone) {
                              return new Intl.DateTimeFormat('en-US', {
                                timeZone: effectiveSchedulingTimezone,
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              }).format(d)
                            }
                            return format(d, 'MM/dd/yyyy, h:mm a')
                          } catch {
                            return format(d, 'MM/dd/yyyy, h:mm a')
                          }
                        })()}
                      </span>
                      <Calendar className="h-4 w-4 shrink-0 text-gray-500" />
                    </div>
                    <input
                      ref={startTimeInputRef}
                      id="agenda-builder-scheduled-start"
                      type="datetime-local"
                      value={formatDateTimeLocal(meeting.startTime)}
                      onChange={(e) => {
                        const val = e.target.value
                        if (!val) {
                          setStartTime(undefined)
                          return
                        }
                        const iso = effectiveSchedulingTimezone
                          ? localStringToUtcInTimezone(val, effectiveSchedulingTimezone)
                          : (() => { const d = new Date(val); return Number.isNaN(d.getTime()) ? null : d.toISOString() })()
                        setStartTime(iso ?? undefined)
                      }}
                      className="absolute inset-0 z-10 h-full min-h-[3.25rem] w-full cursor-pointer opacity-0"
                      aria-label="Scheduled date and time"
                    />
                  </div>
                  {/* Scheduling timezone selector */}
                  <div className="mt-2 relative" ref={tzDropdownRef}>
                    <button
                      type="button"
                      onClick={() => { setTzDropdownOpen((o) => !o); setTzSearch('') }}
                      className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Globe className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate max-w-[min(100vw-4rem,22rem)] text-left" title={schedulingTzButtonLabel}>
                        {schedulingTzButtonLabel}
                      </span>
                      <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${tzDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {tzDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 z-40 min-w-[22rem] max-w-[min(100vw-2rem,36rem)] w-max rounded-xl border border-gray-200 bg-white shadow-lg">
                        <div className="p-2 border-b border-gray-100">
                          <input
                            autoFocus
                            type="text"
                            placeholder="Search by city, region, or GMT…"
                            value={tzSearch}
                            onChange={(e) => setTzSearch(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary-400"
                          />
                        </div>
                        <div className="max-h-52 overflow-y-auto">
                          {filterTimezoneOptions(tzSearch).map((row) => (
                            <button
                              key={row.value}
                              type="button"
                              onClick={() => { setSchedulingTimezone(row.value); setTzDropdownOpen(false); setTzSearch('') }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${row.value === schedulingTimezone ? 'text-primary-600 font-medium bg-primary-50/50' : 'text-gray-700'}`}
                            >
                              <span className="block leading-snug">{row.label}</span>
                              <span className="block text-[11px] text-gray-400 font-normal mt-0.5">{row.value}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-500 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Duration (optional)
                  </label>
                  <div
                    ref={meetingDurationDropdownRef}
                    className="relative inline-block"
                  >
                    <button
                      type="button"
                      onClick={() => setMeetingDurationDropdownOpen((o) => !o)}
                      className={`flex items-center gap-1.5 min-w-[5rem] bg-gray-100 border rounded-lg px-3 py-2 text-sm text-gray-900 hover:border-gray-300 hover:bg-gray-50 ${
                        errors.duration ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      <span>{meeting.duration != null ? meeting.duration : '—'}</span>
                      <span className="text-gray-400">{meeting.duration != null ? 'min' : ''}</span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 shrink-0 transition-transform ${
                          meetingDurationDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {meetingDurationDropdownOpen && (
                      <div className="absolute bottom-full left-0 mb-1 z-30 w-52 rounded-xl border border-gray-200 bg-white shadow-lg py-2">
                        <div className="grid grid-cols-3 gap-1.5 px-2">
                          {MEETING_DURATION_PRESETS.map((mins) => (
                            <button
                              key={mins}
                              type="button"
                              onClick={() => {
                                setMeetingDuration(mins)
                                setMeetingDurationDropdownOpen(false)
                              }}
                              className={`py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                meeting.duration === mins
                                  ? 'bg-primary-100 text-primary-700'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {mins}
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-100 px-2 flex items-center gap-2">
                          <label className="text-xs text-gray-500 whitespace-nowrap">
                            Custom:
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={180}
                            placeholder={String(meeting.duration ?? '')}
                            className="w-14 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center text-gray-900"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const v = parseInt(e.target.value, 10)
                                const clamped = Number.isNaN(v) ? undefined : Math.min(180, Math.max(1, v))
                                setMeetingDuration(clamped)
                                setMeetingDurationDropdownOpen(false)
                              }
                            }}
                            onBlur={(e) => {
                              const v = parseInt(e.target.value, 10)
                              if (!Number.isNaN(v) && v >= 1 && v <= 180) {
                                setMeetingDuration(v)
                              }
                              setMeetingDurationDropdownOpen(false)
                            }}
                          />
                          <span className="text-xs text-gray-400">min</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Total: {totalDuration} minutes
                    {meeting.duration != null && meeting.duration < totalDuration && (
                      <span className="text-red-500 ml-1">
                        · Duration cannot be less than agenda total
                      </span>
                    )}
                  </p>
                  {meeting.endTime && (() => {
                    const d = parseISO(meeting.endTime)
                    return isValid(d) ? (
                      <p className="text-xs text-gray-500 mt-1">
                        Ends at: {effectiveSchedulingTimezone
                          ? new Intl.DateTimeFormat('en-US', { timeZone: effectiveSchedulingTimezone, hour: 'numeric', minute: '2-digit', hour12: true }).format(d)
                          : format(d, 'h:mm a')}
                      </p>
                    ) : null
                  })()}
                  <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={meeting.autoEndMeeting ?? false}
                      onChange={(e) => setMeeting((prev) => ({ ...prev, autoEndMeeting: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      End Zoom call 5 minutes after scheduled end time
                    </span>
                  </label>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={meeting.requireOutputOwnership ?? false}
                      onChange={(e) => setMeeting((prev) => ({ ...prev, requireOutputOwnership: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      Require owner for every captured output
                    </span>
                  </label>
                  {['raghu@rithmo.ai', 'tim@rithmo.ai','charbel@rithmo.ai'].includes(organizerEmailLower) && (
                    <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={meeting.demoMode ?? false}
                        onChange={(e) => setMeeting((prev) => ({ ...prev, demoMode: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">
                        Demo Mode — play first-meeting welcome
                      </span>
                    </label>
                  )}
                  {errors.duration && (
                    <p className="text-sm text-red-500 mt-1">{errors.duration}</p>
                  )}
                  {/* Schedule conflict warning banner */}
                  <ScheduleConflictBanner
                    overlapping={conflictOverlapping}
                    meetingBefore={conflictBefore}
                    meetingAfter={conflictAfter}
                    loading={conflictsLoading}
                  />
                </div>

                {/* Created Meeting - Show after successful creation */}
                {createdMeeting && (
                  <div className="p-5 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-green-800">Meeting Created!</p>
                        <p className="text-sm text-green-600">
                          {Boolean(createdMeeting.cppBot?.isHost)
                            ? 'Rithmo is hosting'
                            : createdMeeting.cppBot?.joined
                              ? 'Rithmo Meeting Manager joined'
                              : 'Join the meeting to get started'}
                          {Boolean(createdMeeting.recallBot?.joined) && ' · Transcription on'}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="p-3 rounded-lg bg-white/80">
                        <p className="text-xs text-gray-500 mb-1">Join URL</p>
                        <p className="text-sm font-mono text-gray-800 break-all">
                          {typeof createdMeeting.joinUrl === 'string' ? createdMeeting.joinUrl : ''}
                        </p>
                      </div>

                      {typeof createdMeeting.password === 'string' && createdMeeting.password !== '' && (
                        <div className="p-3 rounded-lg bg-white/80">
                          <p className="text-xs text-gray-500 mb-1">Password</p>
                          <p className="text-sm font-mono text-gray-800">{createdMeeting.password}</p>
                        </div>
                      )}

                      <button
                        onClick={handleJoinMeeting}
                        disabled={joiningMeeting}
                        className="w-full btn-primary flex items-center justify-center gap-2"
                      >
                        {joiningMeeting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Starting meeting…
                          </>
                        ) : (
                          <>
                            <Video className="w-5 h-5" />
                            Join Meeting Now
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Agenda Items */}
            <div ref={agendaSectionRef} className="glass rounded-2xl p-6 scroll-mt-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {(meeting.audienceMode ?? 'internal') === 'external'
                      ? builderAgendaLens === 'internal'
                        ? 'Internal goals (private)'
                        : 'Public agenda (guest-visible)'
                      : 'Agenda'}
                  </h2>
                  <p className="text-sm text-gray-400">
                    Total: {totalDuration} minutes
                    {(meeting.audienceMode ?? 'internal') !== 'external' ? (
                      <span className="block text-gray-500 mt-1">
                        {`Add your agenda items below. Be sure to allocate the appropriate amount of time for each item. Please note that the Internal Goals option will only appear here if you've selected "Guests outside your team" above.`}
                      </span>
                    ) : builderAgendaLens === 'public' ? (
                      <span className="block text-gray-500 mt-1">
                        Rithmo speaks from this list in the room. Guests see these topics.
                      </span>
                    ) : (
                      <span className="block text-gray-500 mt-1">
                        Optional. Add and link internal goals to public agenda items.
                      </span>
                    )}
                  </p>
                </div>
                {((meeting.audienceMode ?? 'internal') !== 'external' || builderAgendaLens === 'public') && (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <input
                      ref={agendaImportInputRef}
                      type="text"
                      value={agendaImportSearch}
                      onChange={(e) => setAgendaImportSearch(e.target.value)}
                      onFocus={() => {
                        setAgendaImportDropdownOpen(true)
                        if (agendaImportInputRef.current) {
                          setAgendaImportDropdownRect(agendaImportInputRef.current.getBoundingClientRect())
                        }
                      }}
                      onBlur={() => setTimeout(() => setAgendaImportDropdownOpen(false), 150)}
                      placeholder="Import agenda from meeting..."
                      className="input-field pr-10 w-64"
                      disabled={agendaImportingMeeting}
                    />
                    <List className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    {agendaImportDropdownOpen && agendaImportDropdownRect && createPortal(
                      <div
                        className="fixed z-[9999] rounded-xl border border-gray-200 bg-white shadow-xl max-h-60 overflow-auto"
                        style={{
                          left: agendaImportDropdownRect.left,
                          top: agendaImportDropdownRect.bottom + 4,
                          width: Math.max(agendaImportDropdownRect.width, 280),
                          minWidth: 280,
                        }}
                      >
                        {loadingAgendaImportList ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                          </div>
                        ) : (() => {
                          const filtered = agendaImportMeetings.filter((m) => !meetingId || m.meetingId !== meetingId)
                          return filtered.length === 0 ? (
                            <p className="px-4 py-3 text-sm text-gray-500">No meetings found</p>
                          ) : (
                            filtered.map((m) => (
                              <button
                                key={m.meetingId}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault()
                                  handleImportAgendaSelect(m.meetingId)
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100 last:border-0"
                              >
                                <List className="w-4 h-4 text-gray-400 shrink-0" />
                                <span className="truncate text-gray-800">
                                  {decodeHtmlEntities(m.title ?? '') || 'Untitled meeting'}
                                </span>
                              </button>
                            ))
                          )
                        })()}
                      </div>,
                      document.body
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={addAgendaItem}
                    disabled={!canAddAgendaItemWithinBudget}
                    title={
                      !canAddAgendaItemWithinBudget && meeting.duration != null
                        ? `Not enough meeting time left for another ${DEFAULT_NEW_AGENDA_ITEM_MINUTES}-minute topic.`
                        : undefined
                    }
                    className={`btn-secondary text-sm flex items-center gap-2 ${
                      !canAddAgendaItemWithinBudget ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    Add Item
                  </button>
                </div>
                )}
              </div>

              {(meeting.audienceMode ?? 'internal') === 'external' && (
                <div className="mb-4 space-y-2">
                  <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setBuilderAgendaLens('public')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        builderAgendaLens === 'public'
                          ? 'bg-white shadow text-primary-700'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Public agenda
                    </button>
                    <button
                      type="button"
                      onClick={() => setBuilderAgendaLens('internal')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                        builderAgendaLens === 'internal'
                          ? 'bg-white shadow text-primary-700'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Lock className="w-3.5 h-3.5" />
                      Internal goals
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Save or Start applies the public agenda and your internal goals together (internal goals stay private).
                  </p>
                </div>
              )}

              {errors.agenda && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {errors.agenda}
                </div>
              )}

              {((meeting.audienceMode ?? 'internal') !== 'external' || builderAgendaLens === 'public') &&
                (meeting.agenda.length > 0 ? (
                <Reorder.Group
                  axis="y"
                  values={meeting.agenda}
                  onReorder={reorderAgenda}
                  className="space-y-3"
                >
                  {meeting.agenda.map((item, index) => (
                    <Reorder.Item
                      key={item.id}
                      value={item}
                      className="agenda-item group"
                      data-ab-agenda-item-id={item.id}
                    >
                      <div className="agenda-item-handle">
                        <GripVertical className="w-4 h-4" />
                      </div>

                      <div className="flex-1 space-y-3">
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) =>
                            updateAgendaItem(item.id, { title: e.target.value })
                          }
                          placeholder="Agenda item title"
                          className={`w-full bg-transparent border-none outline-none text-gray-900 placeholder:text-gray-400 ${
                            errors[`agenda_${index}`] ? 'text-red-500' : ''
                          }`}
                        />
                        {errors[`agenda_${index}`] && (
                          <p className="text-xs text-red-500">{errors[`agenda_${index}`]}</p>
                        )}
                        <div className="pt-1">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-xs text-gray-500">Details (optional)</span>
                            <div className="flex rounded-lg bg-gray-100 p-0.5" role="group">
                              <button
                                type="button"
                                onClick={() => updateAgendaItem(item.id, { detailsAsBullets: true })}
                                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                  (item.detailsAsBullets !== false)
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                Bullets
                              </button>
                              <button
                                type="button"
                                onClick={() => updateAgendaItem(item.id, { detailsAsBullets: false })}
                                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                  item.detailsAsBullets === false
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                Paragraph
                              </button>
                            </div>
                          </div>
                          {item.detailsAsBullets !== false ? (
                            <div className="space-y-1.5 rounded-lg border border-gray-200 bg-gray-50 p-2 min-h-[4rem]">
                              {(() => {
                                const lines = (item.details ?? '').split('\n')
                                if (lines.length === 0 || lines[lines.length - 1] !== '') lines.push('')
                                return lines.map((line, lineIndex) => (
                                  <div key={lineIndex} className="flex items-center gap-2">
                                    <span className="text-gray-500 text-sm shrink-0 w-4 text-center">•</span>
                                    <input
                                      ref={(el) => {
                                        if (!bulletInputRefs.current[item.id]) bulletInputRefs.current[item.id] = {}
                                        bulletInputRefs.current[item.id][lineIndex] = el
                                      }}
                                      type="text"
                                      value={line}
                                      onChange={(e) => {
                                        const newLines = (item.details ?? '').split('\n')
                                        if (lineIndex >= newLines.length) {
                                          while (newLines.length < lineIndex) newLines.push('')
                                          newLines.push(e.target.value)
                                        } else {
                                          newLines[lineIndex] = e.target.value
                                        }
                                        updateAgendaItem(item.id, { details: newLines.join('\n') })
                                      }}
                                      onKeyDown={(e) => {
                                        const currentLines = (item.details ?? '').split('\n')
                                        if (currentLines.length === 0 || currentLines[currentLines.length - 1] !== '') currentLines.push('')
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          if (lineIndex < currentLines.length - 1) {
                                            bulletInputRefs.current[item.id]?.[lineIndex + 1]?.focus()
                                          } else {
                                            const newLines = [...currentLines]
                                            newLines.splice(lineIndex + 1, 0, '')
                                            updateAgendaItem(item.id, { details: newLines.join('\n') })
                                            setTimeout(() => {
                                              bulletInputRefs.current[item.id]?.[lineIndex + 1]?.focus()
                                            }, 0)
                                          }
                                          return
                                        }
                                        if (e.key === 'Backspace' && line.trim() === '' && lineIndex > 0) {
                                          e.preventDefault()
                                          const newLines = (item.details ?? '').split('\n')
                                          if (newLines.length === 0 || newLines[newLines.length - 1] !== '') newLines.push('')
                                          newLines.splice(lineIndex, 1)
                                          const prevValue = newLines[lineIndex - 1] ?? ''
                                          updateAgendaItem(item.id, { details: newLines.join('\n') })
                                          setTimeout(() => {
                                            const prevEl = bulletInputRefs.current[item.id]?.[lineIndex - 1]
                                            if (prevEl) {
                                              prevEl.focus()
                                              prevEl.setSelectionRange(prevValue.length, prevValue.length)
                                            }
                                          }, 0)
                                        }
                                      }}
                                      placeholder="Add description here"
                                      className="flex-1 min-w-0 text-sm text-gray-700 bg-transparent border-none outline-none placeholder:text-gray-400 focus:ring-0 py-0.5"
                                    />
                                  </div>
                                ))
                              })()}
                            </div>
                          ) : (
                            <textarea
                              value={item.details ?? ''}
                              onChange={(e) => updateAgendaItem(item.id, { details: e.target.value })}
                              placeholder="Add a paragraph (optional)"
                              rows={3}
                              className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-y min-h-[4rem]"
                            />
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 shrink-0 max-w-[12rem]">
                        <div className="flex items-center gap-3">
                          <div
                            ref={durationDropdownItemId === item.id ? durationDropdownRef : null}
                            className="relative flex items-center gap-2 text-gray-500"
                          >
                            <Clock className="w-4 h-4 shrink-0" />
                            <button
                              type="button"
                              onClick={() =>
                                setDurationDropdownItemId((id) => (id === item.id ? null : item.id))
                              }
                              className="flex items-center gap-1.5 min-w-[4.5rem] bg-gray-100 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-900 hover:border-gray-300 hover:bg-gray-50"
                            >
                              <span>{item.duration ?? 5}</span>
                              <span className="text-gray-400">min</span>
                              <ChevronDown
                                className={`w-3.5 h-3.5 shrink-0 transition-transform ${
                                  durationDropdownItemId === item.id ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                            {durationDropdownItemId === item.id && (
                              <div className="absolute bottom-full left-0 mb-1 z-20 w-48 rounded-xl border border-gray-200 bg-white shadow-lg py-2">
                                <div className="grid grid-cols-4 gap-1.5 px-2">
                                  {DURATION_PRESETS.map((mins) => (
                                    <button
                                      key={mins}
                                      type="button"
                                      onClick={() => {
                                        updateAgendaItem(item.id, { duration: mins })
                                        setDurationDropdownItemId(null)
                                      }}
                                      className={`py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                        item.duration === mins
                                          ? 'bg-primary-100 text-primary-700'
                                          : 'text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      {mins}
                                    </button>
                                  ))}
                                </div>
                                <div className="mt-2 pt-2 border-t border-gray-100 px-2 flex items-center gap-2">
                                  <label className="text-xs text-gray-500 whitespace-nowrap">
                                    Custom:
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={120}
                                    placeholder={String(item.duration ?? 5)}
                                    className="w-14 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center text-gray-900"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const v = parseInt(e.target.value, 10)
                                        const clamped = Number.isNaN(v) ? item.duration ?? 5 : Math.min(120, Math.max(1, v))
                                        updateAgendaItem(item.id, { duration: clamped })
                                        setDurationDropdownItemId(null)
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const v = parseInt(e.target.value, 10)
                                      if (!Number.isNaN(v) && v >= 1 && v <= 120) {
                                        updateAgendaItem(item.id, { duration: v })
                                      }
                                      setDurationDropdownItemId(null)
                                    }}
                                  />
                                  <span className="text-xs text-gray-400">min</span>
                                </div>
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => removeAgendaItem(item.id)}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {(() => {
                          const budget = agendaItemTimeBudget(meeting.duration, meeting.agenda, index)
                          if (!budget) {
                            return (
                              <p className="text-[11px] text-gray-400 text-right leading-snug">
                                Set a meeting duration to see how many minutes are left in the budget.
                              </p>
                            )
                          }
                          const slack = Math.round(budget.slackMinutes)
                          const maxThis = Math.max(0, Math.round(budget.maxMinutesThisItem))
                          if (slack < 0) {
                            return (
                              <p className="text-[11px] text-red-600 text-right leading-snug">
                                {Math.abs(slack)} min over the meeting length. Shorten this or other topics
                                before adding more.
                              </p>
                            )
                          }
                          if (slack === 0) {
                            return (
                              <p className="text-[11px] text-amber-700 text-right leading-snug">
                                No minutes left unassigned. Up to {maxThis} min for this topic (others unchanged).
                              </p>
                            )
                          }
                          return (
                            <p className="text-[11px] text-gray-500 text-right leading-snug">
                              {slack} min left unassigned in the meeting. This topic can be up to {maxThis} min
                              without changing others.
                            </p>
                          )
                        })()}
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                  <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 mb-4">No agenda items yet</p>
                  <button
                    type="button"
                    onClick={addAgendaItem}
                    disabled={!canAddAgendaItemWithinBudget}
                    title={
                      !canAddAgendaItemWithinBudget && meeting.duration != null
                        ? `Not enough meeting time left for another ${DEFAULT_NEW_AGENDA_ITEM_MINUTES}-minute topic.`
                        : undefined
                    }
                    className={`btn-primary text-sm inline-flex items-center gap-2 ${
                      !canAddAgendaItemWithinBudget ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    Add First Item
                  </button>
                </div>
              ))}

              {(meeting.audienceMode ?? 'internal') === 'external' && builderAgendaLens === 'internal' && (
                <div className="space-y-4 border-t border-gray-100 pt-6 mt-2">
                  {internalAgendaLoadError && (
                    <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">{internalAgendaLoadError}</p>
                  )}
                  {internalAgendaLoading && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading internal goals…
                    </div>
                  )}
                  {meeting.agenda.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Add at least one public agenda topic first, then link internal goals to those topics.
                    </p>
                  ) : (
                    <>
                      <ul className="space-y-4">
                        {internalGoals.map((g) => (
                          <li
                            key={g.internalAgendaItemId}
                            className="rounded-xl border border-violet-100 bg-violet-50/50 p-4 space-y-3"
                          >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Link to public topic
                                </label>
                                <select
                                  className="input-field text-sm w-full"
                                  value={g.linkedPublicAgendaItemId}
                                  onChange={(e) =>
                                    updateInternalGoalRow(g.internalAgendaItemId, {
                                      linkedPublicAgendaItemId: e.target.value,
                                    })
                                  }
                                >
                                  <option value="">Select a topic…</option>
                                  {meeting.agenda.map((pub) => (
                                    <option key={pub.id} value={pub.id}>
                                      {pub.title?.trim() || 'Untitled topic'}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Goal type
                                </label>
                                <select
                                  className="input-field text-sm w-full"
                                  value={g.desiredOutcome}
                                  onChange={(e) =>
                                    updateInternalGoalRow(g.internalAgendaItemId, {
                                      desiredOutcome: e.target.value,
                                    })
                                  }
                                >
                                  {INTERNAL_DESIRED_OUTCOMES.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Visible to
                                </label>
                                <select
                                  className="input-field text-sm w-full"
                                  value={g.visibilityScope === 'member_private' ? 'member_private' : 'shared_internal_team'}
                                  onChange={(e) =>
                                    updateInternalGoalRow(g.internalAgendaItemId, {
                                      visibilityScope: e.target.value,
                                      authorUserId: e.target.value === 'member_private' ? String(user?.id ?? '') || null : null,
                                    })
                                  }
                                >
                                  <option value="shared_internal_team">Shared internal team</option>
                                  <option value="member_private">Only me</option>
                                </select>
                              </div>
                              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                {g.visibilityScope === 'member_private'
                                  ? 'This goal stays in your personal private layer and will not appear on the team-shared board.'
                                  : 'This goal contributes to shared internal-team live coverage for teammates on the call.'}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Private notes (optional)
                              </label>
                              <textarea
                                value={g.privateNotes}
                                onChange={(e) =>
                                  updateInternalGoalRow(g.internalAgendaItemId, {
                                    privateNotes: e.target.value,
                                  })
                                }
                                rows={2}
                                placeholder="Host-only notes — never shown to guests"
                                className="input-field text-sm resize-y min-h-[3rem]"
                              />
                            </div>
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeInternalGoalRow(g.internalAgendaItemId)}
                                className="text-sm text-red-600 hover:text-red-700 font-medium"
                              >
                                Remove goal
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={addInternalGoalRow}
                        disabled={meeting.agenda.length === 0}
                        className="btn-secondary text-sm inline-flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add internal goal
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col items-end gap-3 pb-12">
              {canEditWholeMeeting ? (
                <div className="flex flex-wrap gap-2 justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving || starting || loadingMeeting}
                    className="btn-secondary flex items-center gap-2"
                  >
                    {saving ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Save className="w-5 h-5" />
                    )}
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleSaveAndSendUpdate}
                    disabled={saving || starting || loadingMeeting}
                    className="btn-primary flex items-center gap-2"
                  >
                    {saving ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    {saving ? 'Sending...' : 'Save and send update to participants'}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={
                      createdMeeting
                        ? handleJoinMeeting
                        : isFormValidForCreate
                          ? handleStartMeeting
                          : (e) => {
                              e.preventDefault()
                              scrollToFirstIncomplete()
                            }
                    }
                    disabled={saving || starting || loadingMeeting}
                    className={`w-full sm:w-auto flex items-center justify-center gap-2 ${
                      !createdMeeting && !isFormValidForCreate
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-75 rounded-lg px-4 py-2'
                        : 'btn-primary'
                    }`}
                  >
                    {starting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : createdMeeting ? (
                      <Video className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5" />
                    )}
                    {starting ? 'Creating...' : createdMeeting ? 'Join Meeting' : 'Create Meeting'}
                  </button>
                  {createdMeeting && (
                    <p className="text-xs text-gray-400">Meeting created! Click to join.</p>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>

      </main>
    </div>

    {concurrentConfirm?.open &&
      createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
          onClick={() => resolveConcurrentConfirm(false)}
        >
          <div
            className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900">
                  You&apos;re already in a Zoom call
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Starting this meeting will end your current Zoom call. Are you sure you want
                  to continue?
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button
                type="button"
                onClick={() => resolveConcurrentConfirm(false)}
                className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => resolveConcurrentConfirm(true)}
                className="btn-primary"
                autoFocus
              >
                End other call &amp; continue
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    {/* Schedule conflict confirmation modal */}
    {scheduleConflictConfirm?.open && (
      <ScheduleConflictModal
        overlapping={conflictOverlapping}
        onConfirm={() => resolveScheduleConflictConfirm(true)}
        onCancel={() => resolveScheduleConflictConfirm(false)}
      />
    )}

    {currentPendingParticipant &&
      createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Confirm participant name</h2>
                {pendingParticipantsQueue.length > 1 && pendingParticipantsTotalRef.current != null && (
                  <p className="mt-0.5 text-xs font-medium text-primary-600">
                    Participant {pendingParticipantsTotalRef.current - pendingParticipantsQueue.length + 1} of {pendingParticipantsTotalRef.current}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  We&apos;ll use this name in the invitation for{' '}
                  <span className="font-medium text-gray-800">{currentPendingParticipant.email}</span>.
                </p>
              </div>
              <button
                type="button"
                onClick={cancelPendingParticipant}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Skip"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4 space-y-2">
              <label className="block text-sm text-gray-600">
                Name to use in email greeting
              </label>
              <input
                type="text"
                value={pendingParticipantName}
                onChange={(e) => setPendingParticipantName(e.target.value)}
                placeholder={currentPendingParticipant.guess || 'Enter participant name'}
                className="input-field"
                autoFocus
              />
              <p className="text-xs text-gray-400">
                This controls the &ldquo;Hi &lt;Name&gt;&rdquo; line in the email invite. You can adjust it if our
                guess isn&apos;t quite right.
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelPendingParticipant}
                className="btn-secondary"
              >
                {pendingParticipantsQueue.length > 1 ? 'Skip' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={confirmPendingParticipant}
                className="btn-primary"
                disabled={!pendingParticipantName.trim() && !currentPendingParticipant.guess}
              >
                Use this name
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      </>
  )
}

export default AgendaBuilderPage
